import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { PaymentForm, CreditCard } from 'react-square-web-payments-sdk';
import { QrReader } from 'react-qr-reader';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  TextField,
  Button,
  Chip,
  Box,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  InputAdornment,
  ButtonGroup,
  Fade,
  Zoom,
  CardActionArea
} from '@mui/material';
import {
  ArrowBack,
  QrCodeScanner,
  Close,
  CalendarToday,
  LocationOn,
  People,
  Event as EventIcon,
  CheckCircle,
  CreditCard as CreditCardIcon,
  MoneyOff,
  Add,
  Remove,
  Person,
  ConfirmationNumber,
  FilterList,
  ExpandMore
} from '@mui/icons-material';
import config from '../config/config';
import CashFloatingWidget from './CashFloatingWidget';
import CashTrackerPanel from './CashTrackerPanel';
import './SellTicketPage.css';
import { CASH_PAYMENTS_ENABLED } from '../config/cashPayments';
import i18n, { intlLocale } from '../i18n/i18n';
import { translateServerMessage } from '../i18n/serverMessages';

// DV-313: cash is withdrawn as a payment method, so every place that seeds or resets the form
// has to start on card instead. One constant rather than sixteen literals, so the flag is the
// single thing to flip if cash ever comes back.
const DEFAULT_PAYMENT_METHOD = CASH_PAYMENTS_ENABLED ? 'CASH' : 'SQUARE';

// For the data-loading effects: the hook's `t` changes with the language, and as an effect
// dependency it would reload the events and teams each time the language is switched.
const tSell = (key, options) => i18n.t(key, { ns: 'sell', ...options });

// The browser's own errors (fetch failing while offline, a reply that isn't JSON) are in English
// whatever language the page is in, so show ours instead. Messages we threw ourselves, already
// translated, pass through.
const errorDetail = (err, fallback) => {
  if (err instanceof TypeError && /fetch|network|load failed/i.test(err.message || '')) {
    return tSell('errors.connection');
  }
  if (err instanceof SyntaxError) {
    return tSell('errors.unexpectedResponse');
  }
  return (err && err.message) || fallback;
};

// The month groups on the event list are keyed by their en-US name ("November 2026"), which the
// page also parses back into a date, so only the heading shown is in the admin's language.
const formatMonthHeading = (monthYear) => {
  const date = new Date(monthYear);
  if (isNaN(date.getTime())) return monthYear;
  const label = date.toLocaleDateString(intlLocale(), { year: 'numeric', month: 'long' });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const SellTicketPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('sell');
  const { getAccessToken } = useAuth();

  // The label shown for a value the API sends as an enum (event type, ticket type, payment
  // method). The value itself is what's compared and sent back; one without a label shows as is.
  const enumLabel = (group, value) =>
    value ? t(`${group}.${String(value).toUpperCase()}`, { defaultValue: value }) : value;
  
  // Mode selection state
  const [sellMode, setSellMode] = useState(null); // 'by-event' or 'by-ibo'
  const [eventFirstStep, setEventFirstStep] = useState('select-event'); // 'select-event' or 'enter-details'
  
  // Filter state for event-first mode
  const [eventFilters, setEventFilters] = useState({
    month: '',
    teamName: '',
    eventType: ''
  });
  
  // Form state
  const [formData, setFormData] = useState({
    iboNumber: '',
    teamName: '',
    eventId: '',
    paymentMethod: DEFAULT_PAYMENT_METHOD
  });
  
  // Ticket selection state
  const [selectedTickets, setSelectedTickets] = useState([]);
  
  // UI state
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingEvents, setFetchingEvents] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [fetchingTeams, setFetchingTeams] = useState(true);
  const [error, setError] = useState(null);
  const [successData, setSuccessData] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  
  // QR Scanner state
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [scannerKey, setScannerKey] = useState(Date.now());
  const activeStreamRef = useRef(null);
  const lastScannedIBORef = useRef(null);
  const isProcessingScanRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const ticketSectionRef = useRef(null);

  // Cash Tracker state
  const [cashTrackerOpen, setCashTrackerOpen] = useState(false);
  const [cashRefreshTrigger, setCashRefreshTrigger] = useState(0);
  
  // User data state
  const [userData, setUserData] = useState(null);
  const [fetchingUserData, setFetchingUserData] = useState(false);
  const [iboValidated, setIboValidated] = useState(false);
  
  // Payment state
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentToken, setPaymentToken] = useState(null);
  const [cardholderName, setCardholderName] = useState('');
  
  // Validation state
  const [validateImmediately, setValidateImmediately] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationTicketData, setValidationTicketData] = useState(null);
  const [validatingTickets, setValidatingTickets] = useState(false);
  const [validationQuantity, setValidationQuantity] = useState(0);
  const [validationError, setValidationError] = useState(null);
  const [validationSuccess, setValidationSuccess] = useState(false);
  const [validatedTicketsInfo, setValidatedTicketsInfo] = useState(null); // Track validated tickets for success page
  
  // Track collapsed months (for event-first mode)
  const [collapsedMonths, setCollapsedMonths] = useState(new Set());
  
  // Toggle month collapse/expand
  const toggleMonthCollapse = (monthYear) => {
    setCollapsedMonths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(monthYear)) {
        newSet.delete(monthYear);
      } else {
        newSet.add(monthYear);
      }
      return newSet;
    });
  };
  
  // Fetch user data by IBO number
  const fetchUserData = async (iboNumber) => {
    try {
      setFetchingUserData(true);
      setError(null);
      
      const token = await getAccessToken();
      if (!token) {
        throw new Error(t('errors.noToken'));
      }
      
      const response = await fetch(`${config.API_BASE_URL}/um/v1/admin/user/get?iboNumber=${iboNumber}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (response.ok && result.status === 'success' && result.data) {
        // User found in system - check if first contract year
        if (result.data.firstContractYear === true) {
          // User is in first contract year - not eligible to purchase tickets
          console.log('User is in first contract year - not eligible for ticket purchase');
          setUserData(result.data);
          setIboValidated(false);
          setError(t('errors.firstContractYear'));
          setFormErrors(prev => ({ 
            ...prev, 
            iboNumber: t('errors.firstContractYearShort')
          }));
          return; // Exit early
        }
        
        // User found in system and eligible
        setUserData(result.data);
        setIboValidated(true);
        
        if (result.data.teamName) {
          setFormData(prev => ({
            ...prev,
            teamName: result.data.teamName
          }));
          // Only filter events in IBO-first mode, not in event-first mode
          if (sellMode !== 'by-event') {
            filterEventsByTeam(result.data.teamName);
          }
        } else {
          // User found but no team - allow manual selection
          setIboValidated(true);
          // Only filter events in IBO-first mode
          if (sellMode !== 'by-event') {
            filterEventsByTeam(null);
          }
        }
        
        setFormErrors(prev => ({ ...prev, iboNumber: null }));
      } else {
        // User not found - validate IBO ticket eligibility with Amway
        console.log('User not found in system, validating IBO ticket eligibility...');
        
        try {
          const eligibilityResponse = await fetch(
            `${config.API_BASE_URL}/um/v1/admin/user/validate-ticket-eligibility?iboNumber=${iboNumber}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              }
            }
          );
          
          const eligibilityResult = await eligibilityResponse.json();
          
          if (eligibilityResponse.ok && eligibilityResult.data) {
            if (eligibilityResult.data.eligible) {
              // IBO is eligible to purchase tickets
              console.log('IBO is eligible for ticket purchase');
              setUserData(null);
              setIboValidated(true);
              setFormData(prev => ({
                ...prev,
                teamName: '' // Clear team name for manual selection
              }));
              
              // Show first contract year warning if applicable
              if (eligibilityResult.data.isFirstContractYear && eligibilityResult.data.additionalInfo) {
                setError(translateServerMessage(eligibilityResult.data.additionalInfo));
                // Clear error after 10 seconds as it's just a warning
                setTimeout(() => setError(null), 10000);
              }
              
              // Only filter events in IBO-first mode
              if (sellMode !== 'by-event') {
                filterEventsByTeam(null);
              }
              setFormErrors(prev => ({ ...prev, iboNumber: null }));
            } else {
              // IBO is not eligible to purchase tickets
              console.log('IBO is not eligible:', eligibilityResult.data.message);
              setUserData(null);
              setIboValidated(false);
              const notEligibleMessage = translateServerMessage(
                eligibilityResult.data.message,
                t('errors.iboNotEligible')
              );
              setError(notEligibleMessage);
              setFormErrors(prev => ({ 
                ...prev, 
                iboNumber: notEligibleMessage
              }));
            }
          } else {
            // Eligibility check failed
            console.error('Failed to validate IBO eligibility');
            setUserData(null);
            setIboValidated(false);
            setError(t('errors.iboValidationFailed'));
            setFormErrors(prev => ({ 
              ...prev, 
              iboNumber: t('errors.iboValidationFailedShort')
            }));
          }
        } catch (eligibilityErr) {
          console.error('Error validating IBO eligibility:', eligibilityErr);
          setUserData(null);
          setIboValidated(false);
          setError(t('errors.iboCheckNumber'));
          setFormErrors(prev => ({ 
            ...prev, 
            iboNumber: t('errors.iboValidationFailedShort')
          }));
        }
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      // Network error or other issue - don't allow proceeding
      setUserData(null);
      setIboValidated(false);
      setError(t('errors.network'));
      setFormErrors(prev => ({ 
        ...prev, 
        iboNumber: t('errors.networkShort')
      }));
    } finally {
      setFetchingUserData(false);
    }
  };
  
  // Filter events based on team eligibility and current date
  const filterEventsByTeam = (teamName) => {
    if (!allEvents || allEvents.length === 0) return;
    
    const now = new Date();
    
    let filtered = allEvents.filter(event => {
      // Filter out past events (check if event end date has passed)
      let eventEndDate;
      try {
        const endDateStr = String(event.endDate).trim();
        const cleanedDateStr = endDateStr.replace(/\s+(PST|PDT|EST|EDT|CST|CDT|MST|MDT|GMT|UTC)$/i, '');
        const match = cleanedDateStr.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
        
        if (match) {
          const [, year, month, day, hour, minute, second] = match;
          eventEndDate = new Date(
            parseInt(year, 10),
            parseInt(month, 10) - 1,
            parseInt(day, 10),
            parseInt(hour, 10),
            parseInt(minute, 10),
            parseInt(second, 10)
          );
        } else {
          eventEndDate = new Date(cleanedDateStr.replace(' ', 'T'));
        }
        
        // Skip if event has ended
        if (eventEndDate < now) {
          return false;
        }
      } catch (error) {
        console.error('Error parsing event end date:', error);
        // If we can't parse the date, include the event to be safe
      }
      
      // Check team eligibility
      // If no eligible teams specified, show to everyone
      if (!event.eligibleTeams || event.eligibleTeams.length === 0) {
        return true;
      }
      
      // If eligible teams includes "all", show to everyone
      if (event.eligibleTeams.some(team => team.toLowerCase() === 'all')) {
        return true;
      }
      
      // If no team is selected yet, show all events (let user select team first)
      if (!teamName || teamName.trim() === '') {
        return true;
      }
      
      // If team is selected, check if event is eligible for that team
      if (event.eligibleTeams.some(team => 
        team.toLowerCase() === teamName.toLowerCase()
      )) {
        return true;
      }
      
      return false;
    });
    
    // Sort events by date (upcoming first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.startDate);
      const dateB = new Date(b.startDate);
      return dateA - dateB;
    });
    
    setEvents(filtered);
    
    // Don't auto-select an event - let user choose
    // Only set if current selection is no longer in filtered list
    if (formData.eventId && !filtered.find(e => e.id === formData.eventId)) {
      setFormData(prev => ({
        ...prev,
        eventId: ''
      }));
      setSelectedTickets([]);
    }
  };
  
  // Fetch events on component mount
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setFetchingEvents(true);
        
        const token = await getAccessToken();
        if (!token) {
          throw new Error(tSell('errors.noToken'));
        }
        
        const response = await fetch(`${config.API_BASE_URL}/em/v1/events/upcoming`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(tSell('errors.fetchEventsStatus', { status: response.status }));
        }
        
        const result = await response.json();
        
        if (result.status === 'success' && result.data) {
          const now = new Date();
          
          // Filter out past events and sort by date (upcoming first)
          const currentEvents = result.data.filter(event => {
            try {
              const endDateStr = String(event.endDate).trim();
              const cleanedDateStr = endDateStr.replace(/\s+(PST|PDT|EST|EDT|CST|CDT|MST|MDT|GMT|UTC)$/i, '');
              const match = cleanedDateStr.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
              
              let eventEndDate;
              if (match) {
                const [, year, month, day, hour, minute, second] = match;
                eventEndDate = new Date(
                  parseInt(year, 10),
                  parseInt(month, 10) - 1,
                  parseInt(day, 10),
                  parseInt(hour, 10),
                  parseInt(minute, 10),
                  parseInt(second, 10)
                );
              } else {
                eventEndDate = new Date(cleanedDateStr.replace(' ', 'T'));
              }
              
              return eventEndDate >= now;
            } catch (error) {
              console.error('Error parsing event end date:', error);
              return true; // Include events with unparseable dates
            }
          }).sort((a, b) => {
            const dateA = new Date(a.startDate);
            const dateB = new Date(b.startDate);
            return dateA - dateB;
          });
          
          setAllEvents(currentEvents);
          setEvents(currentEvents);
        } else {
          throw new Error(translateServerMessage(result.message, tSell('errors.loadEventsFallback')));
        }
      } catch (err) {
        console.error('Error fetching events:', err);
        setError(tSell('errors.loadEvents', { detail: errorDetail(err) }));
      } finally {
        setFetchingEvents(false);
      }
    };
    
    fetchEvents();
  }, [getAccessToken]);
  
  // Fetch teams on component mount
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        setFetchingTeams(true);
        
        const token = await getAccessToken();
        if (!token) {
          throw new Error(tSell('errors.noToken'));
        }
        
        const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/teams`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(tSell('errors.fetchTeamsStatus', { status: response.status }));
        }
        
        const result = await response.json();
        
        if (result.status === 'success' && result.data) {
          setTeams(result.data);
        } else {
          throw new Error(translateServerMessage(result.message, tSell('errors.loadTeamsFallback')));
        }
      } catch (err) {
        console.error('Error fetching teams:', err);
        setError(tSell('errors.loadTeams', { detail: errorDetail(err) }));
      } finally {
        setFetchingTeams(false);
      }
    };
    
    fetchTeams();
  }, [getAccessToken]);
  
  // Get selected event
  // Use allEvents for event-first mode to avoid issues with filtering
  const selectedEvent = (sellMode === 'by-event' ? allEvents : events).find(event => event.id === parseInt(formData.eventId));
  
  // Debug logging
  useEffect(() => {
    if (formData.eventId) {
      console.log('Current eventId:', formData.eventId);
      console.log('Available events:', events.map(e => ({ id: e.id, name: e.name })));
      console.log('Selected event:', selectedEvent ? selectedEvent.name : 'Not found');
      if (selectedEvent) {
        console.log('Ticket price infos:', selectedEvent.ticketPriceInfos);
      }
    }
  }, [formData.eventId, events, selectedEvent]);
  
  // Debug IBO validation state
  useEffect(() => {
    console.log('IBO Validated:', iboValidated);
    console.log('User Data:', userData);
    console.log('Sell Mode:', sellMode);
  }, [iboValidated, userData, sellMode]);
  
  // Initialize collapsed months for event-first mode
  useEffect(() => {
    if (sellMode === 'by-event' && allEvents.length > 0) {
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      
      // Find future and past months (exclude only current month)
      const monthsToCollapse = new Set();
      allEvents.forEach(event => {
        const eventDate = new Date(event.startDate);
        const monthYear = eventDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
        const monthDate = new Date(eventDate.getFullYear(), eventDate.getMonth(), 1);
        const currentMonthDate = new Date(currentYear, currentMonth, 1);
        
        // Collapse all months except current month
        if (monthDate.getTime() !== currentMonthDate.getTime()) {
          monthsToCollapse.add(monthYear);
        }
      });
      
      if (monthsToCollapse.size > 0) {
        setCollapsedMonths(monthsToCollapse);
      }
    }
  }, [sellMode, allEvents]);
  
  // Auto-check "validate immediately" when event is today or in the past
  useEffect(() => {
    // Check selectedEvent first (IBO-First mode)
    if (selectedEvent && isEventTodayOrPast(selectedEvent)) {
      setValidateImmediately(true);
      return;
    }
    
    // Check formData.eventId (Event-First mode)
    if (formData.eventId) {
      const event = allEvents.find(e => e.id === parseInt(formData.eventId, 10));
      if (event && isEventTodayOrPast(event)) {
        setValidateImmediately(true);
        return;
      }
    }
    
    // If no event is today, uncheck
    setValidateImmediately(false);
  }, [selectedEvent, formData.eventId, allEvents]);
  
  // Auto-update validation quantity when tickets change
  useEffect(() => {
    const totalTickets = selectedTickets.reduce((sum, tk) => sum + tk.numberOfTickets, 0);
    setValidationQuantity(totalTickets);
  }, [selectedTickets]);
  
  // Handle QR code scan
  const handleQRScan = (result) => {
    if (result && !isProcessingScanRef.current) {
      let iboNumber = result.text || result;
      
      if (iboNumber.startsWith('T-') || iboNumber.startsWith('GT-')) {
        iboNumber = iboNumber.substring(iboNumber.indexOf('-') + 1);
      }
      
      iboNumber = iboNumber.replace(/\D/g, '');
      
      // Check if this is a new IBO number (not the same as last scanned)
      if (iboNumber && iboNumber !== lastScannedIBORef.current) {
        isProcessingScanRef.current = true;
        lastScannedIBORef.current = iboNumber;
        
        setFormData(prev => ({
          ...prev,
          iboNumber: iboNumber
        }));
        
        closeQRScanner();
        fetchUserData(iboNumber).finally(() => {
          // Reset processing flag after a delay to allow rescanning if needed
          setTimeout(() => {
            isProcessingScanRef.current = false;
          }, 2000);
        });
      }
    }
  };
  
  // Open QR Scanner
  const openQRScanner = async () => {
    try {
      // Reset scan tracking
      lastScannedIBORef.current = null;
      isProcessingScanRef.current = false;
      
      // Request camera permission first
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // Store the stream to stop it later
      activeStreamRef.current = stream;
      
      setShowQRScanner(true);
      setScannerKey(Date.now());
      setError(null);
    } catch (err) {
      console.error('Camera access error:', err);
      setError(t('errors.camera'));
    }
  };
  
  // Close QR Scanner
  const closeQRScanner = () => {
    console.log('Closing QR Scanner - stopping all camera streams');
    
    // Immediately hide the scanner to stop QrReader component
    setShowQRScanner(false);
    
    // Force increment scanner key to ensure fresh instance next time
    setScannerKey(Date.now());
    
    // Stop all media tracks from our ref
    if (activeStreamRef.current) {
      const tracks = activeStreamRef.current.getTracks();
      tracks.forEach(track => {
        console.log('Stopping track:', track.label, track.readyState);
        track.stop();
      });
      activeStreamRef.current = null;
    }
    
    // Aggressively stop ALL video elements in the document
    setTimeout(() => {
      const videoElements = document.querySelectorAll('video');
      console.log('Found video elements:', videoElements.length);
      videoElements.forEach(video => {
        // Pause the video
        video.pause();
        
        // Stop all tracks from srcObject
        if (video.srcObject) {
          const stream = video.srcObject;
          const tracks = stream.getTracks();
          tracks.forEach(track => {
            console.log('Stopping video element track:', track.label, track.readyState);
            track.stop();
          });
          video.srcObject = null;
        }
        
        // Remove src attribute
        video.removeAttribute('src');
        video.load();
      });
      
      // Also try to get all media streams via navigator
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        // Force any active streams to stop
        navigator.mediaDevices.enumerateDevices().then(devices => {
          devices.forEach(device => {
            if (device.kind === 'videoinput') {
              console.log('Found video device:', device.label);
            }
          });
        });
      }
    }, 100);
    
    // Reset processing flag after scanner is closed
    setTimeout(() => {
      isProcessingScanRef.current = false;
    }, 500);
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (activeStreamRef.current) {
        const tracks = activeStreamRef.current.getTracks();
        tracks.forEach(track => track.stop());
      }
      // Reset all refs on unmount
      lastScannedIBORef.current = null;
      isProcessingScanRef.current = false;
    };
  }, []);
  
  // Ensure camera is released when QR scanner dialog closes
  useEffect(() => {
    if (!showQRScanner) {
      // When dialog closes, make sure all camera streams are stopped
      if (activeStreamRef.current) {
        const tracks = activeStreamRef.current.getTracks();
        tracks.forEach(track => {
          if (track.readyState === 'live') {
            track.stop();
          }
        });
        activeStreamRef.current = null;
      }
      
      // Also check for any orphaned video elements
      const videoElements = document.querySelectorAll('video');
      videoElements.forEach(video => {
        if (video.srcObject) {
          const stream = video.srcObject;
          const tracks = stream.getTracks();
          tracks.forEach(track => {
            if (track.readyState === 'live') {
              track.stop();
            }
          });
          video.srcObject = null;
        }
      });
    }
  }, [showQRScanner]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (value.trim()) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
    
    if (name === 'eventId') {
      setSelectedTickets([]);
    }
    
    if (name === 'iboNumber') {
      setIboValidated(false);
      setUserData(null);
      lastScannedIBORef.current = null;
    }
  };
  
  // Handle IBO number validation/fetch
  const handleIBOValidation = () => {
    if (formData.iboNumber && formData.iboNumber.trim()) {
      fetchUserData(formData.iboNumber.trim());
    }
  };
  
  // Handle event selection
  const handleEventSelect = useCallback((event) => {
    console.log('Event selected:', event.id, event.name);
    setFormData(prev => ({ ...prev, eventId: event.id }));
    setSelectedTickets([]);
    
    // Scroll to ticket section after a short delay to allow re-render
    setTimeout(() => {
      if (ticketSectionRef.current) {
        ticketSectionRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest' 
        });
      }
    }, 100);
  }, []);
  
  // Handle ticket selection
  const handleTicketChange = (ticketPriceInfoId, quantity) => {
    setSelectedTickets(prev => {
      const existingIndex = prev.findIndex(ticket => ticket.ticketPriceInfoId === ticketPriceInfoId);
      
      if (quantity === 0) {
        return prev.filter(ticket => ticket.ticketPriceInfoId !== ticketPriceInfoId);
      }
      
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = { ticketPriceInfoId, numberOfTickets: quantity };
        return updated;
      } else {
        return [...prev, { ticketPriceInfoId, numberOfTickets: quantity }];
      }
    });
  };
  
  // Calculate total amount
  const calculateTotal = () => {
    if (!selectedEvent || selectedTickets.length === 0) return 0;
    
    let total = 0;
    selectedTickets.forEach(selectedTicket => {
      const ticketInfo = selectedEvent.ticketPriceInfos.find(
        info => info.id === selectedTicket.ticketPriceInfoId
      );
      if (ticketInfo) {
        const price = getEffectivePrice(ticketInfo);
        const subtotal = price * selectedTicket.numberOfTickets;
        const tax = (subtotal * ticketInfo.taxPercentage) / 100;
        const serviceCharge = ticketInfo.serviceCharge * selectedTicket.numberOfTickets;
        total += subtotal + tax + serviceCharge;
      }
    });
    
    return total;
  };
  
  // Get effective price
  const getEffectivePrice = (ticketInfo) => {
    const now = new Date();
    
    if (ticketInfo.earlyBirdPrice && ticketInfo.earlyBirdEndDate) {
      const earlyBirdEnd = new Date(ticketInfo.earlyBirdEndDate);
      
      if (now <= earlyBirdEnd) {
        return ticketInfo.earlyBirdPrice;
      }
    }
    
    if (ticketInfo.groupDiscountPrice && ticketInfo.minimumGroupSize) {
      const selectedTicket = selectedTickets.find(tk => tk.ticketPriceInfoId === ticketInfo.id);
      if (selectedTicket && selectedTicket.numberOfTickets >= ticketInfo.minimumGroupSize) {
        return ticketInfo.groupDiscountPrice;
      }
    }
    
    return ticketInfo.price;
  };
  
  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!formData.iboNumber.trim()) {
      errors.iboNumber = t('validation.iboRequired');
    } else if (!/^\d+$/.test(formData.iboNumber)) {
      errors.iboNumber = t('validation.iboDigitsOnly');
    }
    
    if (!iboValidated) {
      errors.iboNumber = t('validation.iboNotValidated');
    }
    
    if (!formData.eventId) {
      errors.eventId = t('validation.eventRequired');
    }
    
    if (selectedTickets.length === 0) {
      errors.tickets = t('validation.ticketsRequired');
    }
    
    if (!formData.paymentMethod) {
      errors.paymentMethod = t('validation.paymentMethodRequired');
    }
    
    if (formData.paymentMethod === 'SQUARE' && !cardholderName.trim()) {
      errors.cardholderName = t('validation.cardholderRequired');
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle payment method change
  const handlePaymentMethodChange = (method) => {
    setFormData(prev => ({ ...prev, paymentMethod: method }));
    setShowPaymentForm(method === 'SQUARE');
    setPaymentToken(null);
    setFormErrors(prev => ({ ...prev, paymentMethod: null }));
  };
  
  // Handle Square payment token
  const handleSquareTokenReceived = async (token, verifiedBuyer) => {
    console.log('Square token received:', token);
    setPaymentToken(token.token);
    setError(null);
    
    setTimeout(() => {
      handleFormSubmission(token.token);
    }, 100);
  };
  
  // Separate form submission logic
  const handleFormSubmission = async (squareToken = null) => {
    if (!validateForm()) {
      return;
    }
    
    if (formData.paymentMethod === 'SQUARE' && !squareToken && !paymentToken) {
      setError(t('errors.completeCardForm'));
      return;
    }

    // Guard against duplicate submissions (double-click, or a re-submit while the
    // previous request is still in flight). Backed up by a server-side dedup on the
    // cash purchase endpoint.
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;

    try {
      setLoading(true);
      setError(null);
      
      const token = await getAccessToken();
      if (!token) {
        throw new Error(t('errors.noToken'));
      }
      
      const purchaseData = {
        iboNumber: formData.iboNumber,
        teamName: formData.teamName,
        eventId: parseInt(formData.eventId, 10),
        paymentMethod: formData.paymentMethod,
        selectedTickets: selectedTickets
      };
      
      if (formData.paymentMethod === 'SQUARE') {
        purchaseData.paymentToken = squareToken || paymentToken;
        purchaseData.cardholderName = cardholderName;
      }
      
      const response = await fetch(`${config.API_BASE_URL}/em/v1/ticket/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(purchaseData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(translateServerMessage(data.message, t('errors.httpStatus', { status: response.status })));
      }
      
      // Check if we should show validation modal
      // Find the purchased event from allEvents using formData.eventId
      const purchasedEvent = allEvents.find(e => e.id === parseInt(formData.eventId, 10));
      
      console.log('Purchase completed - Validation check:', {
        validateImmediately,
        validationQuantity,
        purchasedEvent: purchasedEvent ? purchasedEvent.name : 'not found',
        eventDate: purchasedEvent ? purchasedEvent.startDate : null
      });
      
      if (validateImmediately && purchasedEvent && validationQuantity > 0) {
        // Only allow validation if the event is today or in the past
        if (!isEventTodayOrPast(purchasedEvent)) {
          console.log('Validation skipped: event is not on the same day as purchase');
          setValidatedTicketsInfo({
            validated: false,
            skipped: true,
            count: validationQuantity,
            reason: t('success.skippedReason', { name: purchasedEvent.name })
          });
          setSuccessData(data.data);
        } else {
          // Validate tickets immediately without showing modal
          const isGenericTicket = purchasedEvent.eventType === 'OPEN';
          
          const ticketCode = data.data.tickets && data.data.tickets.length > 0
            ? data.data.tickets[0].ticketCode
            : null;
          
          console.log('Validating tickets immediately:', {
            validationQuantity,
            isGenericTicket,
            ticketCode: ticketCode ? 'present' : 'missing',
            eventType: purchasedEvent.eventType
          });
          
          // Validate tickets in the background
          validateTicketsAfterPurchase(ticketCode, isGenericTicket, validationQuantity)
            .then(() => {
              console.log('Tickets validated successfully');
              setValidatedTicketsInfo({
                validated: true,
                count: validationQuantity
              });
            })
            .catch((error) => {
              console.error('Validation failed:', error);
            })
            .finally(() => {
              setSuccessData(data.data);
            });
        }
      } else {
        // No validation needed, show success page immediately
        setSuccessData(data.data);
      }

      // Trigger cash tracker refresh if it was a cash sale
      if (formData.paymentMethod === 'CASH') {
        setCashRefreshTrigger(prev => prev + 1);
      }
      
    } catch (err) {
      console.error('Error purchasing ticket:', err);
      setError(errorDetail(err));
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };
  
  // Navigate back to dashboard
  const handleBack = () => {
    // Always close QR scanner if it's open
    if (showQRScanner) {
      closeQRScanner();
    }
    
    if (sellMode === 'by-event' && eventFirstStep === 'enter-details') {
      // Go back to event selection - reset IBO validation state
      setEventFirstStep('select-event');
      setIboValidated(false);
      setUserData(null);
      setFormData(prev => ({
        ...prev,
        iboNumber: '',
        teamName: ''
      }));
      setSelectedTickets([]);
      lastScannedIBORef.current = null;
      isProcessingScanRef.current = false;
    } else if (sellMode) {
      // Go back to mode selection - reset all state
      setSellMode(null);
      setFormData({
        iboNumber: '',
        teamName: '',
        eventId: '',
        paymentMethod: DEFAULT_PAYMENT_METHOD
      });
      setSelectedTickets([]);
      setEventFilters({ month: '', teamName: '', eventType: '' });
      setIboValidated(false);
      setUserData(null);
      lastScannedIBORef.current = null;
      isProcessingScanRef.current = false;
    } else {
      // Go back to dashboard
      navigate('/');
    }
  };
  
  // Handle mode selection
  const handleModeSelect = (mode) => {
    // Close QR scanner if it's open
    if (showQRScanner) {
      closeQRScanner();
    }
    
    // Reset all state when selecting a mode
    setFormData({
      iboNumber: '',
      teamName: '',
      eventId: '',
      paymentMethod: DEFAULT_PAYMENT_METHOD
    });
    setSelectedTickets([]);
    setIboValidated(false);
    setUserData(null);
    setFormErrors({});
    setError(null);
    setSuccessData(null);
    lastScannedIBORef.current = null;
    isProcessingScanRef.current = false;
    
    setSellMode(mode);
    if (mode === 'by-event') {
      setEventFirstStep('select-event');
      setEventFilters({ month: '', teamName: '', eventType: '' });
    }
  };
  
  // Handle event selection in event-first mode
  const handleEventFirstSelect = (event) => {
    // Close QR scanner if it's open
    if (showQRScanner) {
      closeQRScanner();
    }
    
    setFormData(prev => ({ ...prev, eventId: event.id }));
    setEventFirstStep('enter-details');
    // Clear any previous validation state
    setIboValidated(false);
    setUserData(null);
    setFormErrors({});
    setError(null);
    setSelectedTickets([]);
  };
  
  // Get filtered events for event-first mode
  const getFilteredEventsForEventFirst = () => {
    let filtered = [...allEvents];
    
    // Filter by month
    if (eventFilters.month) {
      filtered = filtered.filter(event => {
        const eventDate = new Date(event.startDate);
        const monthYear = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}`;
        return monthYear === eventFilters.month;
      });
    }
    
    // Filter by team
    if (eventFilters.teamName) {
      filtered = filtered.filter(event => {
        if (!event.eligibleTeams || event.eligibleTeams.length === 0) return true;
        if (event.eligibleTeams.some(team => team.toLowerCase() === 'all')) return true;
        return event.eligibleTeams.some(team => team.toLowerCase() === eventFilters.teamName.toLowerCase());
      });
    }
    
    // Filter by event type
    if (eventFilters.eventType) {
      filtered = filtered.filter(event => event.eventType && event.eventType.toLowerCase() === eventFilters.eventType.toLowerCase());
    }
    
    return filtered;
  };
  
  // Get unique months from events
  const getAvailableMonths = () => {
    const months = new Set();
    allEvents.forEach(event => {
      const eventDate = new Date(event.startDate);
      const monthYear = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}`;
      months.add(monthYear);
    });
    return Array.from(months).sort();
  };
  
  // Get unique event types - use predefined types plus any additional from events
  const getEventTypes = () => {
    const predefinedTypes = ['CONFERENCE', 'SEMINAR', 'OPEN', 'WORKSHOP'];
    const typesSet = new Set(predefinedTypes);
    
    // Add any additional types from events that aren't in predefined list
    allEvents.forEach(event => {
      if (event.eventType) {
        typesSet.add(event.eventType.toUpperCase());
      }
    });
    
    // Return in the order: predefined first, then additional ones
    return Array.from(typesSet);
  };
  
  // Purchase another ticket
  const handlePurchaseAnother = () => {
    // Close QR scanner if it's open
    if (showQRScanner) {
      closeQRScanner();
    }
    
    setSuccessData(null);
    setSelectedTickets([]);
    setPaymentToken(null);
    setCardholderName('');
    setShowPaymentForm(false);
    setIboValidated(false);
    setUserData(null);
    setFormErrors({});
    setError(null);
    setValidatedTicketsInfo(null); // Clear validation info for new sale
    lastScannedIBORef.current = null;
    isProcessingScanRef.current = false;
    
    // If in event-first mode, keep the event selected and go back to enter details
    if (sellMode === 'by-event') {
      // Preserve the eventId when resetting for another customer
      setFormData(prev => ({
        iboNumber: '',
        teamName: '',
        eventId: prev.eventId, // Keep the event selected
        paymentMethod: DEFAULT_PAYMENT_METHOD
      }));
      setEventFirstStep('enter-details');
    } else {
      // IBO-first mode: stay in the same mode, just reset form
      // (sellMode stays as 'by-ibo', user stays on the IBO entry page)
      setFormData({
        iboNumber: '',
        teamName: '',
        eventId: '',
        paymentMethod: DEFAULT_PAYMENT_METHOD
      });
    }
  };
  
  // Format date
  const formatDate = (dateString, timezone = 'America/Los_Angeles') => {
    if (!dateString) return t('common.invalidDate');
    
    try {
      // API sends ISO 8601 format dates (e.g., "2025-05-15T21:30:00.000Z")
      const date = new Date(dateString);
      
      if (isNaN(date.getTime())) {
        console.warn('Invalid date format:', dateString);
        return t('common.invalidDate');
      }
      
      return date.toLocaleDateString(intlLocale(), {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: timezone
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return t('common.invalidDate');
    }
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(intlLocale(), {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  
  // Get event type color
  const getEventTypeColor = (eventType) => {
    switch(eventType?.toUpperCase()) {
      case 'CONFERENCE': return '#4285F4'; // Blue
      case 'SEMINAR': return '#34A853';    // Green
      case 'OPEN': return '#9C27B0';       // Purple
      case 'WORKSHOP': return '#FF6B6B';   // Red/Coral
      default: return '#6366f1';           // Default Indigo
    }
  };
  
  // Check if event date is today or in the past (for immediate validation)
  const isEventTodayOrPast = (event) => {
    if (!event || !event.startDate) return false;
    
    const eventDate = new Date(event.startDate);
    const today = new Date();
    
    // Use event timezone if available
    const timezone = event.timezone || 'America/Los_Angeles';
    
    // Set both dates to start of day for accurate comparison
    const eventDateAtMidnight = new Date(eventDate.toLocaleDateString('en-US', { timeZone: timezone }));
    const todayAtMidnight = new Date(today.toLocaleDateString('en-US', { timeZone: timezone }));
    
    // Event date is today or in the past
    return eventDateAtMidnight <= todayAtMidnight;
  };
  
  // Validate tickets immediately after purchase (without modal)
  const validateTicketsAfterPurchase = async (ticketCode, isGenericTicket, quantity) => {
    if (!ticketCode || quantity <= 0) {
      throw new Error('Invalid ticket code or quantity');
    }
    
    const token = await getAccessToken();
    if (!token) {
      throw new Error(t('errors.noToken'));
    }
    
    console.log(`Validating ${quantity} ${isGenericTicket ? 'generic' : 'regular'} tickets using ticket code`);
    
    // Validate each ticket individually by calling the API multiple times
    for (let i = 0; i < quantity; i++) {
      const validateUrl = `${config.API_BASE_URL}/em/v1/ticket/validate?ticketQRCode=${encodeURIComponent(ticketCode)}&isGenericTicket=${isGenericTicket}`;
      console.log(`Calling validation API for ticket ${i + 1}/${quantity}`);
      
      const response = await fetch(validateUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (!response.ok || result.status !== 'success') {
        throw new Error(`Validation failed for ticket ${i + 1}: ${result.message || 'Unknown error'}`);
      }
      
      console.log(`Ticket ${i + 1}/${quantity} validated successfully`);
    }
    
    console.log(`All ${quantity} tickets validated successfully`);
  };
  
  // Handle ticket validation after purchase
  const handleValidateTickets = async () => {
    if (!validationTicketData) {
      console.log('No validation ticket data available');
      return;
    }
    
    if (!validationTicketData.ticketCode) {
      setValidationError(t('errors.ticketCodeMissing'));
      return;
    }
    
    try {
      setValidatingTickets(true);
      setValidationError(null);
      
      const token = await getAccessToken();
      if (!token) {
        throw new Error(t('errors.noToken'));
      }
      
      // Use ticketCode for both generic and regular tickets
      // Validate each ticket individually by calling the API multiple times
      const isGenericTicket = validationTicketData.isGenericTicket;
      console.log(`Validating ${validationQuantity} ${isGenericTicket ? 'generic' : 'regular'} tickets using ticket code`);
      
      for (let i = 0; i < validationQuantity; i++) {
        const validateUrl = `${config.API_BASE_URL}/em/v1/ticket/validate?ticketQRCode=${encodeURIComponent(validationTicketData.ticketCode)}&isGenericTicket=${isGenericTicket}`;
        console.log(`Calling validation API for ticket ${i + 1}/${validationQuantity}:`, validateUrl);
        
        const response = await fetch(
          validateUrl,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        const result = await response.json();
        
        if (!response.ok || result.status !== 'success') {
          throw new Error(t('errors.validationFailedForTicket', {
            number: i + 1,
            detail: translateServerMessage(result.message, t('errors.unknown'))
          }));
        }
        
        console.log(`Ticket ${i + 1}/${validationQuantity} validated successfully`);
      }
      
      setValidationSuccess(true);
      console.log(`All ${validationQuantity} tickets validated successfully`);
      
      // Store validation info for success page
      setValidatedTicketsInfo({
        validated: true,
        count: validationQuantity
      });
      
      // Close modal after 2 seconds and show success page
      setTimeout(() => {
        // Show success page with the stored purchase data
        if (validationTicketData.purchaseData) {
          setSuccessData(validationTicketData.purchaseData);
        }
        
        setShowValidationModal(false);
        setValidationSuccess(false);
        setValidationTicketData(null);
      }, 2000);
      
    } catch (err) {
      console.error('Error validating tickets:', err);
      setValidationError(errorDetail(err, t('errors.validateTicketsFallback')));
    } finally {
      setValidatingTickets(false);
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.paymentMethod === 'CASH') {
      handleFormSubmission();
    }
  };
  
  // If events are loading
  if (fetchingEvents) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }
  
  // Mode Selection Landing Page
  if (!sellMode) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 4 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBack}
              sx={{ mb: 2 }}
            >
              {t('mode.backToDashboard')}
            </Button>
            <Typography variant="h3" fontWeight="bold" gutterBottom textAlign="center">
              {t('mode.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" textAlign="center">
              {t('mode.subtitle')}
            </Typography>
          </Box>
          
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={6}>
              <Zoom in={true} timeout={400}>
                <Card 
                  sx={{ 
                    height: '100%',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <CardActionArea onClick={() => handleModeSelect('by-event')} sx={{ height: '100%', p: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <EventIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h4" fontWeight="bold" gutterBottom>
                        {t('mode.byEvent.title')}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                        {t('mode.byEvent.description')}
                      </Typography>
                      <Box sx={{ mt: 3 }}>
                        <Chip label={t('mode.byEvent.chipEventFirst')} color="primary" sx={{ mr: 1 }} />
                        <Chip label={t('mode.byEvent.chipBrowse')} variant="outlined" />
                      </Box>
                    </Box>
                  </CardActionArea>
                </Card>
              </Zoom>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Zoom in={true} timeout={600}>
                <Card 
                  sx={{ 
                    height: '100%',
                    transition: 'all 0.3s',
                    opacity: 0.5,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      pointerEvents: 'none'
                    }
                  }}
                >
                  <CardActionArea disabled sx={{ height: '100%', p: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Person sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
                      <Typography variant="h4" fontWeight="bold" gutterBottom color="text.disabled">
                        {t('mode.byIbo.title')}
                      </Typography>
                      <Typography variant="body1" color="text.disabled" sx={{ mt: 2 }}>
                        {t('mode.byIbo.description')}
                      </Typography>
                      <Box sx={{ mt: 3 }}>
                        <Chip label={t('mode.byIbo.chipCustomerFirst')} disabled sx={{ mr: 1 }} />
                        <Chip label={t('mode.byIbo.chipQrReady')} disabled variant="outlined" />
                      </Box>
                      <Chip 
                        label={t('mode.byIbo.comingSoon')} 
                        size="small" 
                        sx={{ 
                          mt: 2, 
                          backgroundColor: 'grey.300', 
                          color: 'grey.700',
                          fontWeight: 'bold'
                        }} 
                      />
                    </Box>
                  </CardActionArea>
                </Card>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  // If ticket sale was successful
  if (successData) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Zoom in={true}>
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircle color="success" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h4" gutterBottom fontWeight="bold">
              {t('success.title')}
            </Typography>
            
            {sellMode === 'by-event' ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <Trans t={t} i18nKey="success.tipByEvent" components={{ strong: <strong /> }} />
                </Typography>
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <Trans t={t} i18nKey="success.tipByIbo" components={{ strong: <strong /> }} />
                </Typography>
              </Alert>
            )}
            
            <Paper elevation={0} sx={{ bgcolor: 'grey.100', p: 3, my: 3 }}>
              <Typography variant="overline" color="text.secondary">
                {t('success.confirmationCode')}
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="primary">
                {successData.purchaseConfirmationCode}
              </Typography>
            </Paper>
            
            {/* Show validation status if tickets were validated */}
            {validatedTicketsInfo && validatedTicketsInfo.validated && (
              <Alert 
                severity="success" 
                icon={<CheckCircle />}
                sx={{ 
                  mb: 3,
                  '& .MuiAlert-message': {
                    width: '100%'
                  }
                }}
              >
                <Box>
                  <Typography variant="body1" fontWeight="bold" gutterBottom>
                    ✓ {t('common.ticketsValidated')}
                  </Typography>
                  <Typography variant="body2">
                    <Trans
                      t={t}
                      i18nKey="success.validatedBody"
                      count={validatedTicketsInfo.count}
                      components={{ strong: <strong /> }}
                    />
                  </Typography>
                </Box>
              </Alert>
            )}
            
            {/* Show validation skipped message */}
            {validatedTicketsInfo && validatedTicketsInfo.skipped && (
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 3,
                  '& .MuiAlert-message': {
                    width: '100%'
                  }
                }}
              >
                <Box>
                  <Typography variant="body1" fontWeight="bold" gutterBottom>
                    {t('success.skippedTitle')}
                  </Typography>
                  <Typography variant="body2">
                    {validatedTicketsInfo.reason}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {t('success.skippedHint')}
                  </Typography>
                </Box>
              </Alert>
            )}
            
            <Stack spacing={2} sx={{ textAlign: 'left', mb: 3 }}>
              <Box display="flex" justifyContent="space-between">
                <Typography color="text.secondary">{t('success.event')}</Typography>
                <Typography fontWeight="bold">{successData.eventName}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography color="text.secondary">{t('success.iboNumber')}</Typography>
                <Typography fontWeight="bold">{successData.iboNumber}</Typography>
              </Box>
              {successData.teamName && (
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">{t('success.team')}</Typography>
                  <Typography fontWeight="bold">{successData.teamName}</Typography>
                </Box>
              )}
              <Box display="flex" justifyContent="space-between">
                <Typography color="text.secondary">{t('success.totalTickets')}</Typography>
                <Typography fontWeight="bold">{successData.totalTickets}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography color="text.secondary">{t('success.paymentMethod')}</Typography>
                <Typography fontWeight="bold">{enumLabel('paymentMethods', successData.paymentMethod)}</Typography>
              </Box>
              <Divider />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6">{t('success.totalAmount')}</Typography>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  {formatCurrency(successData.total)}
                </Typography>
              </Box>
            </Stack>
            
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
              <Button
                variant="contained"
                size="large"
                onClick={handlePurchaseAnother}
                startIcon={<ConfirmationNumber />}
              >
                {sellMode === 'by-event' ? t('success.sellToAnotherCustomer') : t('success.sellAnotherTicket')}
              </Button>
              {sellMode === 'by-event' ? (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    // Close QR scanner if it's open
                    if (showQRScanner) {
                      closeQRScanner();
                    }
                    setSuccessData(null);
                    setSelectedTickets([]);
                    setPaymentToken(null);
                    setCardholderName('');
                    setShowPaymentForm(false);
                    setIboValidated(false);
                    setUserData(null);
                    lastScannedIBORef.current = null;
                    isProcessingScanRef.current = false;
                    setFormData({
                      iboNumber: '',
                      teamName: '',
                      eventId: '',
                      paymentMethod: DEFAULT_PAYMENT_METHOD
                    });
                    setEventFirstStep('select-event');
                  }}
                  startIcon={<EventIcon />}
                >
                  {t('success.changeEvent')}
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    // Close QR scanner if it's open
                    if (showQRScanner) {
                      closeQRScanner();
                    }
                    setSuccessData(null);
                    setSelectedTickets([]);
                    setPaymentToken(null);
                    setCardholderName('');
                    setShowPaymentForm(false);
                    setIboValidated(false);
                    setUserData(null);
                    lastScannedIBORef.current = null;
                    isProcessingScanRef.current = false;
                    setFormData({
                      iboNumber: '',
                      teamName: '',
                      eventId: '',
                      paymentMethod: DEFAULT_PAYMENT_METHOD
                    });
                    setSellMode(null);
                  }}
                  startIcon={<Person />}
                >
                  {t('success.changeSellMode')}
                </Button>
              )}
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/')}
              >
                {t('success.returnToDashboard')}
              </Button>
            </Stack>
          </Paper>
        </Zoom>

        {/* Cash Tracker Floating Widget & Panel */}
        <CashFloatingWidget
          onClick={() => setCashTrackerOpen(true)}
          refreshTrigger={cashRefreshTrigger}
        />
        <CashTrackerPanel
          open={cashTrackerOpen}
          onClose={() => setCashTrackerOpen(false)}
          refreshTrigger={cashRefreshTrigger}
        />
      </Container>
    );
  }
  
  // Event-First Mode - Event Selection
  if (sellMode === 'by-event' && eventFirstStep === 'select-event') {
    const filteredEvents = getFilteredEventsForEventFirst();
    const availableMonths = getAvailableMonths();
    const eventTypes = getEventTypes();
    
    // Get current month and year
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Group events by month and categorize them
    const eventsByMonth = filteredEvents.reduce((acc, event) => {
      const eventDate = new Date(event.startDate);
      const monthYear = eventDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      if (!acc[monthYear]) {
        acc[monthYear] = [];
      }
      acc[monthYear].push(event);
      return acc;
    }, {});
    
    // Categorize months as current, future, or past
    const categorizedMonths = Object.keys(eventsByMonth).reduce((acc, monthYear) => {
      const monthDate = new Date(monthYear);
      const monthKey = monthDate.getMonth();
      const yearKey = monthDate.getFullYear();
      
      if (yearKey === currentYear && monthKey === currentMonth) {
        acc.current = monthYear;
      } else if (monthDate > new Date(currentYear, currentMonth, 1)) {
        acc.future.push(monthYear);
      } else {
        acc.past.push(monthYear);
      }
      return acc;
    }, { current: null, future: [], past: [] });
    
    // Sort future and past months
    categorizedMonths.future.sort((a, b) => new Date(a) - new Date(b));
    categorizedMonths.past.sort((a, b) => new Date(b) - new Date(a)); // Most recent past first
    
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
        <Container maxWidth="xl">
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBack}
              sx={{ mb: 2 }}
            >
              {t('common.back')}
            </Button>
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              {t('selectEvent.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('selectEvent.subtitle')}
            </Typography>
          </Box>
          
          {/* Filters */}
          <Box className="sell-ticket-filter-bar">
            {/* Month Filter */}
            <Box className="sell-ticket-filter-group">
              <span className="sell-ticket-filter-label">
                <CalendarToday sx={{ fontSize: 16 }} />
                {t('selectEvent.filters.month')}
              </span>
              <button
                className={`sell-ticket-filter-btn ${!eventFilters.month ? 'active' : ''}`}
                onClick={() => setEventFilters(prev => ({ ...prev, month: '' }))}
              >
                {t('common.all')}
              </button>
              {availableMonths.map(month => {
              // Parse year and month from the string (format: "YYYY-MM")
              const [year, monthNum] = month.split('-').map(Number);
              // Create date in local time (monthNum - 1 because JS months are 0-indexed)
              const date = new Date(year, monthNum - 1, 1);
                const shortLabel = date.toLocaleDateString(intlLocale(), { year: 'numeric', month: 'short' });
                const label = shortLabel.charAt(0).toUpperCase() + shortLabel.slice(1);
                return (
                  <button
                    key={month}
                    className={`sell-ticket-filter-btn ${eventFilters.month === month ? 'active' : ''}`}
                    onClick={() => setEventFilters(prev => ({ ...prev, month }))}
                  >
                    {label}
                  </button>
                );
              })}
            </Box>

            {/* Team Filter */}
            <Box className="sell-ticket-filter-group">
              <span className="sell-ticket-filter-label">
                <People sx={{ fontSize: 16 }} />
                {t('selectEvent.filters.team')}
              </span>
              <button
                className={`sell-ticket-filter-btn ${!eventFilters.teamName ? 'active' : ''}`}
                onClick={() => setEventFilters(prev => ({ ...prev, teamName: '' }))}
              >
                {t('common.all')}
              </button>
              {teams.map(team => (
                <button
                  key={team.id}
                  className={`sell-ticket-filter-btn ${eventFilters.teamName === team.name ? 'active' : ''}`}
                  onClick={() => setEventFilters(prev => ({ ...prev, teamName: team.name }))}
                >
                  {team.name}
                </button>
              ))}
            </Box>

            {/* Event Type Filter */}
            <Box className="sell-ticket-filter-group">
              <span className="sell-ticket-filter-label">
                <FilterList sx={{ fontSize: 16 }} />
                {t('selectEvent.filters.type')}
              </span>
              <button
                className={`sell-ticket-filter-btn ${!eventFilters.eventType ? 'active' : ''}`}
                onClick={() => setEventFilters(prev => ({ ...prev, eventType: '' }))}
              >
                {t('common.all')}
              </button>
              {eventTypes.map(type => (
                <button
                  key={type}
                  className={`sell-ticket-filter-btn ${eventFilters.eventType === type ? 'active' : ''}`}
                  onClick={() => setEventFilters(prev => ({ ...prev, eventType: type }))}
                >
                  {enumLabel('eventTypes', type)}
                </button>
              ))}
            </Box>

            {/* Clear Filters */}
            {(eventFilters.month || eventFilters.teamName || eventFilters.eventType) && (
              <button 
                className="sell-ticket-clear-filters-btn"
                onClick={() => setEventFilters({ month: '', teamName: '', eventType: '' })}
              >
                {t('selectEvent.filters.clearAll')}
              </button>
            )}
          </Box>
          
          {/* Events Grouped by Month */}
          {Object.keys(eventsByMonth).length > 0 ? (
            <>
              {/* Current Month - Expanded by default */}
              {categorizedMonths.current && (() => {
                const monthYear = categorizedMonths.current;
                const isCollapsed = collapsedMonths.has(monthYear);
                return (
              <Box key={monthYear} sx={{ mb: 4 }}>
                  <Box 
                    sx={{ 
                  mb: 2, 
                  pb: 1.5, 
                  borderBottom: '2px solid rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                      flexWrap: 'wrap',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        borderRadius: 1
                      }
                    }}
                    onClick={() => toggleMonthCollapse(monthYear)}
                  >
                    {/* Collapse/Expand Icon */}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(25, 118, 210, 0.1)',
                      transition: 'transform 0.3s',
                      transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)'
                    }}>
                      <ExpandMore sx={{ color: '#1976d2', fontSize: 20 }} />
                    </Box>
                    
                  <Typography 
                    variant="h5" 
                    fontWeight="bold"
                    sx={{
                      background: 'linear-gradient(135deg, #1976d2 0%, #ec407a 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      color: 'transparent',
                      fontSize: { xs: '1.25rem', md: '1.5rem' }
                    }}
                  >
                    {formatMonthHeading(monthYear)}
                  </Typography>
                  <Chip
                    label={t('selectEvent.eventCount', { count: eventsByMonth[monthYear].length })}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(25, 118, 210, 0.15)',
                      color: '#1976d2',
                      fontWeight: 'bold',
                      border: '1px solid rgba(25, 118, 210, 0.3)',
                      fontSize: { xs: '0.7rem', md: '0.75rem' }
                    }}
                  />
                </Box>
                  
                  {/* Conditionally render events grid based on collapse state */}
                  {!isCollapsed && (
                <Grid container spacing={{ xs: 0.75, sm: 1, md: 1.5 }}>
                  {eventsByMonth[monthYear].map(event => (
                    <Grid item xs={6} sm={6} md={4} lg={3} key={event.id} sx={{ display: 'flex' }}>
                      <Fade in={true} timeout={500}>
                        <Card
                          sx={{
                            cursor: 'pointer',
                            transition: 'all 0.3s',
                            overflow: 'hidden',
                            width: '100%',
                            minHeight: { xs: 220, sm: 300, md: 340 },
                            display: 'flex',
                            flexDirection: 'column',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: 4
                            }
                          }}
                          onClick={() => handleEventFirstSelect(event)}
                        >
                          <Box sx={{ position: 'relative' }}>
                            <CardMedia
                              component="img"
                              sx={{ height: { xs: 120, sm: 160, md: 180 } }}
                              image={event.imageUrl || `https://via.placeholder.com/400x200?text=${encodeURIComponent(event.name)}`}
                              alt={event.name}
                            />
                            {/* Overlay badges on image */}
                            <Box sx={{ position: 'absolute', top: 8, left: 8, right: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Chip
                                label={enumLabel('eventTypes', event.eventType)}
                                size="small"
                                sx={{
                                  backgroundColor: getEventTypeColor(event.eventType),
                                  color: 'white',
                                  fontWeight: 'bold',
                                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                  height: { xs: 20, sm: 24 },
                                  textTransform: 'uppercase'
                                }}
                              />
                            </Box>
                          </Box>
                          <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 2 }, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <Box>
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                              <CalendarToday sx={{ fontSize: { xs: 12, sm: 14 }, color: 'primary.main' }} />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                {new Date(event.startDate).toLocaleDateString(intlLocale(), { 
                                  weekday: 'short', 
                                  month: 'short', 
                                  day: 'numeric',
                                  hour: 'numeric',
                                    minute: '2-digit',
                                    timeZone: event.timezone || 'America/Los_Angeles'
                                })}
                              </Typography>
                            </Box>
                            <Typography 
                              variant="subtitle1" 
                              fontWeight="bold" 
                              gutterBottom 
                              sx={{ 
                                fontSize: { xs: '0.85rem', sm: '1rem' },
                                lineHeight: 1.3,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                  minWidth: 0,
                                  minHeight: { xs: '2.2rem', sm: '2.6rem' }
                              }}
                            >
                              {event.name}
                            </Typography>
                            </Box>
                            <Box>
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5} sx={{ minWidth: 0 }}>
                              <LocationOn sx={{ fontSize: { xs: 12, sm: 14 }, color: 'text.secondary', flexShrink: 0 }} />
                              <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                noWrap
                                sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                              >
                                {event.location}
                              </Typography>
                            </Box>
                              {(!event.ticketPriceInfos || event.ticketPriceInfos.length === 0) ? (
                              <Chip
                                label={t('selectEvent.noTickets')}
                                size="small"
                                color="warning"
                                sx={{ 
                                  height: { xs: 18, sm: 20 },
                                  fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                  mt: 0.5
                                }}
                              />
                              ) : (
                                <Box sx={{ height: { xs: 18, sm: 20 }, mt: 0.5 }} />
                            )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Fade>
                    </Grid>
                  ))}
                </Grid>
                  )}
              </Box>
                );
              })()}
              
              {/* Future Months - Collapsed by default */}
              {categorizedMonths.future.map(monthYear => {
                const isCollapsed = collapsedMonths.has(monthYear);
                return (
                  <Box key={monthYear} sx={{ mb: 4 }}>
                    <Box 
                      sx={{ 
                        mb: 2, 
                        pb: 1.5, 
                        borderBottom: '2px solid rgba(0, 0, 0, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                          borderRadius: 1
                        }
                      }}
                      onClick={() => toggleMonthCollapse(monthYear)}
                    >
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                        transition: 'transform 0.3s',
                        transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)'
                      }}>
                        <ExpandMore sx={{ color: '#1976d2', fontSize: 20 }} />
                      </Box>
                      
                      <Typography 
                        variant="h5" 
                        fontWeight="bold"
                        sx={{
                          background: 'linear-gradient(135deg, #1976d2 0%, #ec407a 100%)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          color: 'transparent',
                          fontSize: { xs: '1.25rem', md: '1.5rem' }
                        }}
                      >
                        {formatMonthHeading(monthYear)}
                      </Typography>
                      <Chip
                        label={t('selectEvent.eventCount', { count: eventsByMonth[monthYear].length })}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(25, 118, 210, 0.15)',
                          color: '#1976d2',
                          fontWeight: 'bold',
                          border: '1px solid rgba(25, 118, 210, 0.3)',
                          fontSize: { xs: '0.7rem', md: '0.75rem' }
                        }}
                      />
                    </Box>
                    
                    {!isCollapsed && (
                      <Grid container spacing={{ xs: 0.75, sm: 1, md: 1.5 }}>
                        {eventsByMonth[monthYear].map(event => (
                          <Grid item xs={6} sm={6} md={4} lg={3} key={event.id} sx={{ display: 'flex' }}>
                            <Fade in={true} timeout={500}>
                              <Card
                                sx={{
                                  cursor: 'pointer',
                                  transition: 'all 0.3s',
                                  overflow: 'hidden',
                                  width: '100%',
                                  minHeight: { xs: 220, sm: 300, md: 340 },
                                  display: 'flex',
                                  flexDirection: 'column',
                                  '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 4
                                  }
                                }}
                                onClick={() => handleEventFirstSelect(event)}
                              >
                                <Box sx={{ position: 'relative' }}>
                                  <CardMedia
                                    component="img"
                                    sx={{ height: { xs: 120, sm: 160, md: 180 } }}
                                    image={event.imageUrl || `https://via.placeholder.com/400x200?text=${encodeURIComponent(event.name)}`}
                                    alt={event.name}
                                  />
                                  <Box sx={{ position: 'absolute', top: 8, left: 8, right: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Chip
                                      label={enumLabel('eventTypes', event.eventType)}
                                      size="small"
                                      sx={{
                                        backgroundColor: getEventTypeColor(event.eventType),
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                        height: { xs: 20, sm: 24 },
                                        textTransform: 'uppercase'
                                      }}
                                    />
                                  </Box>
                                </Box>
                                <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 2 }, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                  <Box>
                                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                      <CalendarToday sx={{ fontSize: { xs: 12, sm: 14 }, color: 'primary.main' }} />
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                        {new Date(event.startDate).toLocaleDateString(intlLocale(), { 
                                          weekday: 'short', 
                                          month: 'short', 
                                          day: 'numeric',
                                          hour: 'numeric',
                                          minute: '2-digit',
                                          timeZone: event.timezone || 'America/Los_Angeles'
                                        })}
                                      </Typography>
                                    </Box>
                                    <Typography 
                                      variant="subtitle1" 
                                      fontWeight="bold" 
                                      gutterBottom 
                                      sx={{ 
                                        fontSize: { xs: '0.85rem', sm: '1rem' },
                                        lineHeight: 1.3,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        minWidth: 0,
                                        minHeight: { xs: '2.2rem', sm: '2.6rem' }
                                      }}
                                    >
                                      {event.name}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5} sx={{ minWidth: 0 }}>
                                      <LocationOn sx={{ fontSize: { xs: 12, sm: 14 }, color: 'text.secondary', flexShrink: 0 }} />
                                      <Typography 
                                        variant="caption" 
                                        color="text.secondary" 
                                        noWrap
                                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                                      >
                                        {event.location}
                                      </Typography>
                                    </Box>
                                    {(!event.ticketPriceInfos || event.ticketPriceInfos.length === 0) ? (
                                      <Chip
                                        label={t('selectEvent.noTickets')}
                                        size="small"
                                        color="warning"
                                        sx={{ 
                                          height: { xs: 18, sm: 20 },
                                          fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                          mt: 0.5
                                        }}
                                      />
                                    ) : (
                                      <Box sx={{ height: { xs: 18, sm: 20 }, mt: 0.5 }} />
                                    )}
                                  </Box>
                                </CardContent>
                              </Card>
                            </Fade>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Box>
                );
              })}
              
              {/* Past Events Section */}
              {categorizedMonths.past.length > 0 && (
                <Box sx={{ mt: 6 }}>
                  <Box sx={{ 
                    mb: 3, 
                    pb: 2, 
                    borderBottom: '3px solid rgba(158, 158, 158, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}>
                    <Typography 
                      variant="h4" 
                      fontWeight="bold"
                      sx={{
                        color: '#757575',
                        fontSize: { xs: '1.5rem', md: '2rem' }
                      }}
                    >
                      📅 {t('selectEvent.pastEvents')}
                    </Typography>
                    <Chip
                      label={t('selectEvent.eventCount', { count: categorizedMonths.past.reduce((sum, m) => sum + eventsByMonth[m].length, 0) })}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(117, 117, 117, 0.15)',
                        color: '#757575',
                        fontWeight: 'bold',
                        border: '1px solid rgba(117, 117, 117, 0.3)',
                        fontSize: { xs: '0.7rem', md: '0.75rem' }
                      }}
                    />
                  </Box>
                  
                  {categorizedMonths.past.map(monthYear => {
                    const isCollapsed = collapsedMonths.has(monthYear);
                    return (
                      <Box key={monthYear} sx={{ mb: 4 }}>
                        <Box 
                          sx={{ 
                            mb: 2, 
                            pb: 1.5, 
                            borderBottom: '2px solid rgba(0, 0, 0, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            flexWrap: 'wrap',
                            cursor: 'pointer',
                            transition: 'all 0.3s',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.02)',
                              borderRadius: 1
                            }
                          }}
                          onClick={() => toggleMonthCollapse(monthYear)}
                        >
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(117, 117, 117, 0.1)',
                            transition: 'transform 0.3s',
                            transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)'
                          }}>
                            <ExpandMore sx={{ color: '#757575', fontSize: 20 }} />
                          </Box>
                          
                          <Typography 
                            variant="h5" 
                            fontWeight="bold"
                            sx={{
                              color: '#757575',
                              fontSize: { xs: '1.25rem', md: '1.5rem' }
                            }}
                          >
                            {formatMonthHeading(monthYear)}
                          </Typography>
                          <Chip
                            label={t('selectEvent.eventCount', { count: eventsByMonth[monthYear].length })}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(117, 117, 117, 0.15)',
                              color: '#757575',
                              fontWeight: 'bold',
                              border: '1px solid rgba(117, 117, 117, 0.3)',
                              fontSize: { xs: '0.7rem', md: '0.75rem' }
                            }}
                          />
                        </Box>
                        
                        {!isCollapsed && (
                          <Grid container spacing={{ xs: 0.75, sm: 1, md: 1.5 }}>
                            {eventsByMonth[monthYear].map(event => (
                              <Grid item xs={6} sm={6} md={4} lg={3} key={event.id} sx={{ display: 'flex' }}>
                                <Fade in={true} timeout={500}>
                                  <Card
                                    sx={{
                                      cursor: 'pointer',
                                      transition: 'all 0.3s',
                                      overflow: 'hidden',
                                      width: '100%',
                                      minHeight: { xs: 220, sm: 300, md: 340 },
                                      display: 'flex',
                                      flexDirection: 'column',
                                      opacity: 0.85,
                                      '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: 4,
                                        opacity: 1
                                      }
                                    }}
                                    onClick={() => handleEventFirstSelect(event)}
                                  >
                                    <Box sx={{ position: 'relative' }}>
                                      <CardMedia
                                        component="img"
                                        sx={{ height: { xs: 120, sm: 160, md: 180 }, filter: 'grayscale(30%)' }}
                                        image={event.imageUrl || `https://via.placeholder.com/400x200?text=${encodeURIComponent(event.name)}`}
                                        alt={event.name}
                                      />
                                      <Box sx={{ position: 'absolute', top: 8, left: 8, right: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Chip
                                          label={enumLabel('eventTypes', event.eventType)}
                                          size="small"
                                          sx={{
                                            backgroundColor: getEventTypeColor(event.eventType),
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                            height: { xs: 20, sm: 24 },
                                            textTransform: 'uppercase',
                                            opacity: 0.8
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                    <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 2 }, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                      <Box>
                                        <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                          <CalendarToday sx={{ fontSize: { xs: 12, sm: 14 }, color: 'text.secondary' }} />
                                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                            {new Date(event.startDate).toLocaleDateString(intlLocale(), { 
                                              weekday: 'short', 
                                              month: 'short', 
                                              day: 'numeric',
                                              hour: 'numeric',
                                              minute: '2-digit',
                                              timeZone: event.timezone || 'America/Los_Angeles'
                                            })}
                                          </Typography>
                                        </Box>
                                        <Typography 
                                          variant="subtitle1" 
                                          fontWeight="bold" 
                                          gutterBottom 
                                          sx={{ 
                                            fontSize: { xs: '0.85rem', sm: '1rem' },
                                            lineHeight: 1.3,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            minWidth: 0,
                                            minHeight: { xs: '2.2rem', sm: '2.6rem' }
                                          }}
                                        >
                                          {event.name}
                                        </Typography>
                                      </Box>
                                      <Box>
                                        <Box display="flex" alignItems="center" gap={0.5} mb={0.5} sx={{ minWidth: 0 }}>
                                          <LocationOn sx={{ fontSize: { xs: 12, sm: 14 }, color: 'text.secondary', flexShrink: 0 }} />
                                          <Typography 
                                            variant="caption" 
                                            color="text.secondary" 
                                            noWrap
                                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                                          >
                                            {event.location}
                                          </Typography>
                                        </Box>
                                        {(!event.ticketPriceInfos || event.ticketPriceInfos.length === 0) ? (
                                          <Chip
                                            label={t('selectEvent.noTickets')}
                                            size="small"
                                            color="warning"
                                            sx={{ 
                                              height: { xs: 18, sm: 20 },
                                              fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                              mt: 0.5
                                            }}
                                          />
                                        ) : (
                                          <Box sx={{ height: { xs: 18, sm: 20 }, mt: 0.5 }} />
                                        )}
                                      </Box>
                                    </CardContent>
                                  </Card>
                                </Fade>
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </>
          ) : (
            <Paper sx={{ p: 6, textAlign: 'center' }}>
              <EventIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {t('selectEvent.empty')}
              </Typography>
              <Button 
                sx={{ mt: 2 }}
                onClick={() => setEventFilters({ month: '', teamName: '', eventType: '' })}
              >
                {t('selectEvent.clearFilters')}
              </Button>
            </Paper>
          )}
        </Container>

        {/* Cash Tracker Floating Widget & Panel */}
        <CashFloatingWidget
          onClick={() => setCashTrackerOpen(true)}
          refreshTrigger={cashRefreshTrigger}
        />
        <CashTrackerPanel
          open={cashTrackerOpen}
          onClose={() => setCashTrackerOpen(false)}
          refreshTrigger={cashRefreshTrigger}
        />
      </Box>
    );
  }
  
  // Event-First Mode - Enter IBO Details (after event selection)
  if (sellMode === 'by-event' && eventFirstStep === 'enter-details') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
        <Container maxWidth="xl">
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBack}
              sx={{ mb: 2 }}
            >
              {t('details.backToEventSelection')}
            </Button>
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              {t('details.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('details.subtitle', { name: selectedEvent ? selectedEvent.name : t('details.thisEvent') })}
            </Typography>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          {/* Validation Modal */}
          {showValidationModal && validationTicketData && (
            <Dialog
              open={true}
              onClose={() => {
                if (!validatingTickets) {
                  // Show success page with the stored purchase data when modal is closed
                  if (validationTicketData?.purchaseData) {
                    setSuccessData(validationTicketData.purchaseData);
                  }
                  setShowValidationModal(false);
                  setValidationTicketData(null);
                }
              }}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CheckCircle />
                  <span>{t('validationModal.title')}</span>
                </Box>
                {!validatingTickets && !validationSuccess && (
                  <IconButton
                    onClick={() => {
                      // Show success page with the stored purchase data when X is clicked
                      if (validationTicketData?.purchaseData) {
                        setSuccessData(validationTicketData.purchaseData);
                      }
                      setShowValidationModal(false);
                      setValidationTicketData(null);
                    }}
                    sx={{ 
                      position: 'absolute', 
                      right: 8, 
                      top: 8,
                      color: 'white'
                    }}
                  >
                    <Close />
                  </IconButton>
                )}
              </DialogTitle>
              <DialogContent sx={{ mt: 3 }}>
                {validationSuccess ? (
                  <Box textAlign="center" py={4}>
                    <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                    <Typography variant="h5" gutterBottom fontWeight="bold" color="success.main">
                      {t('common.ticketsValidated')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {t('validationModal.validatedCount', { count: validationQuantity })}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Alert severity="info" sx={{ mb: 3 }}>
                      <Trans t={t} i18nKey="validationModal.purchaseSuccess" components={{ strong: <strong /> }} />
                    </Alert>
                    
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body1" gutterBottom>
                        <Trans
                          t={t}
                          i18nKey="validationModal.totalPurchased"
                          values={{ total: validationTicketData.totalTickets }}
                          components={{ strong: <strong /> }}
                        />
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('validationModal.selectHowMany')}
                      </Typography>
                    </Box>
                    
                    <TextField
                      fullWidth
                      type="number"
                      label={t('validationModal.quantityLabel')}
                      value={validationQuantity}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        setValidationQuantity(Math.min(Math.max(0, value), validationTicketData.totalTickets));
                      }}
                      inputProps={{
                        min: 0,
                        max: validationTicketData.totalTickets
                      }}
                      helperText={t('validationModal.quantityHelper', { max: validationTicketData.totalTickets })}
                      sx={{ mb: 3 }}
                      disabled={validatingTickets}
                    />
                    
                    {validationError && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {validationError}
                      </Alert>
                    )}
                    
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        onClick={() => {
                          // Show success page with the stored purchase data
                          if (validationTicketData?.purchaseData) {
                            setSuccessData(validationTicketData.purchaseData);
                          }
                          setShowValidationModal(false);
                          setValidationTicketData(null);
                        }}
                        disabled={validatingTickets}
                      >
                        {t('validationModal.skip')}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleValidateTickets}
                        disabled={validatingTickets || validationQuantity <= 0}
                        startIcon={validatingTickets ? <CircularProgress size={20} /> : <CheckCircle />}
                      >
                        {validatingTickets ? t('validationModal.validating') : t('validationModal.validateButton', { count: validationQuantity })}
                      </Button>
                    </Stack>
                  </>
                )}
              </DialogContent>
            </Dialog>
          )}
          
          {/* QR Scanner Dialog */}
          {showQRScanner && (
            <Dialog
              open={true}
              onClose={closeQRScanner}
              maxWidth="sm"
              fullWidth
              disableEscapeKeyDown={false}
              TransitionProps={{
                onExited: () => {
                  // Final cleanup after dialog animation completes
                  console.log('Dialog exited - final cleanup');
                  const videoElements = document.querySelectorAll('video');
                  videoElements.forEach(video => {
                    if (video.srcObject) {
                      const stream = video.srcObject;
                      stream.getTracks().forEach(track => track.stop());
                      video.srcObject = null;
                    }
                  });
                }
              }}
            >
              <DialogTitle>
                {t('qr.title')}
                <IconButton
                  onClick={closeQRScanner}
                  sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                  <Close />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <Box sx={{ position: 'relative', width: '100%', height: 400, bgcolor: 'black', borderRadius: 2, overflow: 'hidden' }}>
                  <QrReader
                    key={scannerKey}
                    constraints={{
                      facingMode: 'environment',
                      width: { ideal: 1280 },
                      height: { ideal: 720 }
                    }}
                    onResult={(result, error) => {
                      if (result) {
                        handleQRScan(result);
                      }
                    }}
                    scanDelay={300}
                    videoId="sell-ticket-qr-video"
                    videoStyle={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      position: 'absolute',
                      top: 0,
                      left: 0
                    }}
                  />
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 200,
                    height: 200,
                    border: '3px solid',
                    borderColor: 'primary.main',
                    borderRadius: 2,
                    pointerEvents: 'none',
                    zIndex: 10
                  }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                {t('qr.hint')}
              </Typography>
            </DialogContent>
          </Dialog>
          )}
          
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', lg: 'row' },
              gap: 3,
              alignItems: 'flex-start'
            }}
          >
            {/* Left Section - IBO and Team (70%) */}
            <Box 
              sx={{
                flex: { xs: '1 1 100%', lg: '0 0 calc(70% - 12px)' },
                minWidth: 0,
                width: { xs: '100%', lg: 'calc(70% - 12px)' }
              }}
            >
              <Stack spacing={3}>
                {/* Selected Event Info */}
                {selectedEvent && (
                  <Card>
                    <CardContent>
                      <Typography variant="overline" color="text.secondary">
                        {t('details.selectedEvent')}
                      </Typography>
                      <Box display="flex" gap={2} alignItems="center" mt={1}>
                        {selectedEvent.imageUrl && (
                          <Box
                            component="img"
                            src={selectedEvent.imageUrl}
                            alt={selectedEvent.name}
                            sx={{ width: 80, height: 80, borderRadius: 1, objectFit: 'cover' }}
                          />
                        )}
                        <Box flex={1}>
                          <Typography variant="h6" fontWeight="bold">
                            {selectedEvent.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(selectedEvent.startDate, selectedEvent.timezone)} • {selectedEvent.location}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                )}
                
                {/* IBO Number Card */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      {t('ibo.label')}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        fullWidth
                        name="iboNumber"
                        value={formData.iboNumber}
                        onChange={handleChange}
                        placeholder={t('ibo.placeholder')}
                        disabled={fetchingUserData}
                        error={!!formErrors.iboNumber}
                        helperText={formErrors.iboNumber}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={openQRScanner}
                                disabled={fetchingUserData}
                                color="primary"
                              >
                                <QrCodeScanner />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                      {formData.iboNumber && !iboValidated && (
                        <Button
                          variant="contained"
                          onClick={handleIBOValidation}
                          disabled={fetchingUserData}
                          sx={{ minWidth: 120 }}
                        >
                          {fetchingUserData ? <CircularProgress size={24} /> : t('common.validate')}
                        </Button>
                      )}
                    </Stack>
                    {iboValidated && (
                      <Alert 
                        severity={userData ? "success" : "info"} 
                        sx={{ mt: 2 }} 
                        icon={<CheckCircle />}
                      >
                        {userData 
                          ? t('ibo.valid', { name: userData.iboName || userData.email })
                          : t('ibo.validatedSelectTeam')}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
                
                {/* Team Name Card */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      {t('team.label')}
                    </Typography>
                    
                    {iboValidated && userData && userData.teamName ? (
                      <>
                        <Chip
                          label={userData.teamName}
                          color="primary"
                          size="large"
                          sx={{ fontSize: '1rem', py: 2.5, px: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          {t('team.fromProfile')}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                          {teams.map(team => (
                            <Chip
                              key={team.id}
                              label={team.name}
                              onClick={() => {
                                setFormData(prev => ({ ...prev, teamName: team.name }));
                              }}
                              color={formData.teamName === team.name ? 'primary' : 'default'}
                              variant={formData.teamName === team.name ? 'filled' : 'outlined'}
                              sx={{
                                fontSize: '0.95rem',
                                py: 2.5,
                                px: 2,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: 2
                                }
                              }}
                              disabled={!iboValidated}
                            />
                          ))}
                        </Box>
                        {!iboValidated && (
                          <Typography variant="caption" color="text.secondary">
                            {t('team.validateIboFirst')}
                          </Typography>
                        )}
                        {iboValidated && teams.length === 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {t('team.noTeams')}
                          </Typography>
                        )}
                        {iboValidated && teams.length > 0 && !formData.teamName && (
                          <Typography variant="caption" color="warning.main">
                            {t('team.selectTeam')}
                          </Typography>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </Stack>
            </Box>
            
            {/* Right Section - Tickets & Payment (30%) - Reuse from IBO flow */}
            <Box 
              sx={{
                flex: { xs: '1 1 100%', lg: '0 0 calc(30% - 12px)' },
                minWidth: 0,
                width: { xs: '100%', lg: 'calc(30% - 12px)' }
              }}
            >
              <Box sx={{ position: { lg: 'sticky' }, top: { lg: 100 } }}>
                <Stack spacing={3}>
                  {/* No Tickets Available Message */}
                  {selectedEvent && (!selectedEvent.ticketPriceInfos || selectedEvent.ticketPriceInfos.length === 0) && (
                    <Card>
                      <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <ConfirmationNumber sx={{ fontSize: 60, color: 'warning.main', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          {t('tickets.noneTitle')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('tickets.noneBody')}
                        </Typography>
                        <Button 
                          variant="outlined" 
                          sx={{ mt: 2 }}
                          onClick={() => {
                            setFormData({
                              iboNumber: '',
                              teamName: '',
                              eventId: '',
                              paymentMethod: DEFAULT_PAYMENT_METHOD
                            });
                            setIboValidated(false);
                            setUserData(null);
                            setSelectedTickets([]);
                            setFormErrors({});
                            setError(null);
                            setEventFirstStep('select-event');
                          }}
                        >
                          {t('tickets.chooseDifferentEvent')}
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Ticket Selection Card */}
                  {selectedEvent && selectedEvent.ticketPriceInfos && selectedEvent.ticketPriceInfos.length > 0 && (
                    <Fade in={true} timeout={500}>
                      <Card ref={ticketSectionRef}>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6" fontWeight="bold">
                              {t('tickets.title')}
                            </Typography>
                            {iboValidated ? (
                              <Chip 
                                icon={<CheckCircle />} 
                                label={t('ibo.verified')} 
                                color="success" 
                                size="small" 
                              />
                            ) : (
                              <Chip 
                                label={t('ibo.awaiting')} 
                                color="warning" 
                                size="small" 
                              />
                            )}
                          </Box>
                          {!iboValidated && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                              <Trans t={t} i18nKey="ibo.validateFirstAlert" components={{ strong: <strong /> }} />
                            </Alert>
                          )}
                          <Stack spacing={2}>
                            {selectedEvent.ticketPriceInfos
                              .filter(ticket => ticket.active)
                              .sort((a, b) => a.displayOrder - b.displayOrder)
                              .map(ticketInfo => {
                                const effectivePrice = getEffectivePrice(ticketInfo);
                                const selectedTicket = selectedTickets.find(tk => tk.ticketPriceInfoId === ticketInfo.id);
                                const quantity = selectedTicket ? selectedTicket.numberOfTickets : 0;
                                
                                return (
                                  <Paper 
                                    key={ticketInfo.id} 
                                    elevation={1} 
                                    sx={{ 
                                      p: 2,
                                      opacity: iboValidated ? 1 : 0.6,
                                      transition: 'opacity 0.3s'
                                    }}
                                  >
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                      <Typography variant="subtitle2" fontWeight="bold">
                                        {ticketInfo.name}
                                      </Typography>
                                      <Typography variant="subtitle1" color="primary" fontWeight="bold">
                                        {formatCurrency(effectivePrice)}
                                      </Typography>
                                    </Box>
                                    {ticketInfo.description && (
                                      <Typography variant="caption" color="text.secondary" mb={1} display="block">
                                        {ticketInfo.description}
                                      </Typography>
                                    )}
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                                      <Chip label={enumLabel('ticketTypes', ticketInfo.ticketType)} size="small" />
                                      <Box display="flex" alignItems="center" gap={0.5}>
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            console.log('Decrement clicked, iboValidated:', iboValidated);
                                            handleTicketChange(ticketInfo.id, Math.max(0, quantity - 1));
                                          }}
                                          disabled={!iboValidated || quantity === 0}
                                          color="primary"
                                          sx={{
                                            border: 1,
                                            borderColor: (!iboValidated || quantity === 0) ? 'grey.300' : 'primary.main',
                                            '&:hover': {
                                              backgroundColor: 'primary.light'
                                            }
                                          }}
                                        >
                                          <Remove fontSize="small" />
                                        </IconButton>
                                        <Typography 
                                          variant="h6" 
                                          fontWeight="bold" 
                                          sx={{ 
                                            minWidth: 35, 
                                            textAlign: 'center',
                                            color: iboValidated ? 'primary.main' : 'text.disabled'
                                          }}
                                        >
                                          {quantity}
                                        </Typography>
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            console.log('Increment clicked, iboValidated:', iboValidated);
                                            handleTicketChange(ticketInfo.id, Math.min(ticketInfo.availableQuantity, quantity + 1));
                                          }}
                                          disabled={!iboValidated || quantity >= ticketInfo.availableQuantity}
                                          color="primary"
                                          sx={{
                                            border: 1,
                                            borderColor: (!iboValidated || quantity >= ticketInfo.availableQuantity) ? 'grey.300' : 'primary.main',
                                            '&:hover': {
                                              backgroundColor: 'primary.light'
                                            }
                                          }}
                                        >
                                          <Add fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                      {t('tickets.available', { count: ticketInfo.availableQuantity })}
                                    </Typography>
                                  </Paper>
                                );
                              })}
                          </Stack>
                          {formErrors.tickets && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                              {formErrors.tickets}
                            </Alert>
                          )}
                        </CardContent>
                      </Card>
                    </Fade>
                  )}
                  
                  {/* Payment Summary Card */}
                  {selectedEvent && iboValidated && (
                    <Fade in={true} timeout={700}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom fontWeight="bold">
                            {t('summary.title')}
                          </Typography>
                          
                          {selectedTickets.length > 0 ? (
                            <>
                              <Stack spacing={1} sx={{ mb: 2 }}>
                                {selectedTickets.map(selectedTicket => {
                                  const ticketInfo = selectedEvent.ticketPriceInfos.find(
                                    info => info.id === selectedTicket.ticketPriceInfoId
                                  );
                                  if (!ticketInfo) return null;
                                  
                                  const effectivePrice = getEffectivePrice(ticketInfo);
                                  const subtotal = effectivePrice * selectedTicket.numberOfTickets;
                                  const tax = (subtotal * ticketInfo.taxPercentage) / 100;
                                  const serviceCharge = ticketInfo.serviceCharge * selectedTicket.numberOfTickets;
                                  
                                  return (
                                    <Box key={selectedTicket.ticketPriceInfoId}>
                                      <Box display="flex" justifyContent="space-between">
                                        <Typography variant="body2">
                                          {t('summary.lineItem', { name: ticketInfo.name, quantity: selectedTicket.numberOfTickets })}
                                        </Typography>
                                        <Typography variant="body2" fontWeight="bold">
                                          {formatCurrency(subtotal)}
                                        </Typography>
                                      </Box>
                                      {tax > 0 && (
                                        <Box display="flex" justifyContent="space-between" ml={2}>
                                          <Typography variant="caption" color="text.secondary">
                                            {t('summary.tax', { percent: ticketInfo.taxPercentage })}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {formatCurrency(tax)}
                                          </Typography>
                                        </Box>
                                      )}
                                      {serviceCharge > 0 && (
                                        <Box display="flex" justifyContent="space-between" ml={2}>
                                          <Typography variant="caption" color="text.secondary">
                                            {t('summary.serviceCharge')}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {formatCurrency(serviceCharge)}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  );
                                })}
                              </Stack>
                              
                              <Divider sx={{ my: 2 }} />
                              
                              <Box display="flex" justifyContent="space-between" mb={3}>
                                <Typography variant="h6" fontWeight="bold">
                                  {t('summary.total')}
                                </Typography>
                                <Typography variant="h6" color="primary" fontWeight="bold">
                                  {formatCurrency(calculateTotal())}
                                </Typography>
                              </Box>
                              
                              {/* Validate Immediately Checkbox - Only enabled if event is today or in the past */}
                              {(() => {
                                let eventIsToday = false;
                                if (selectedEvent) {
                                  eventIsToday = isEventTodayOrPast(selectedEvent);
                                } else if (formData.eventId) {
                                  const event = allEvents.find(e => e.id === parseInt(formData.eventId, 10));
                                  eventIsToday = event ? isEventTodayOrPast(event) : false;
                                }
                                
                                return (
                                  <Box 
                                    sx={{ 
                                      mb: 2, 
                                      p: 2, 
                                      bgcolor: !eventIsToday ? 'rgba(0, 0, 0, 0.04)' : validateImmediately ? 'rgba(76, 175, 80, 0.08)' : 'rgba(0, 0, 0, 0.02)',
                                      borderRadius: 2,
                                      border: !eventIsToday ? '1px solid rgba(0, 0, 0, 0.08)' : validateImmediately ? '1px solid rgba(76, 175, 80, 0.3)' : '1px solid rgba(0, 0, 0, 0.12)',
                                      opacity: !eventIsToday ? 0.7 : 1
                                    }}
                                  >
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <input
                                        type="checkbox"
                                        id="validate-immediately"
                                        checked={eventIsToday && validateImmediately}
                                        disabled={!eventIsToday}
                                        onChange={(e) => setValidateImmediately(e.target.checked)}
                                        style={{
                                          width: '18px',
                                          height: '18px',
                                          cursor: eventIsToday ? 'pointer' : 'not-allowed',
                                          accentColor: '#4CAF50'
                                        }}
                                      />
                                      <label 
                                        htmlFor="validate-immediately" 
                                        style={{ 
                                          cursor: eventIsToday ? 'pointer' : 'not-allowed', 
                                          fontWeight: 500,
                                          color: !eventIsToday ? '#999' : validateImmediately ? '#2E7D32' : '#666',
                                          fontSize: '0.95rem',
                                          userSelect: 'none'
                                        }}
                                      >
                                        {t('validateNow.checkbox')}
                                      </label>
                                    </Box>
                                    <Typography variant="caption" sx={{ ml: 3.5, mt: 0.5, display: 'block', color: !eventIsToday ? 'error.main' : 'text.secondary' }}>
                                      {eventIsToday
                                        ? t('validateNow.availableToday')
                                        : t('validateNow.onlyOnEventDay')}
                                    </Typography>
                                  </Box>
                                );
                              })()}
                              
                              {/* Validation Quantity Selector - Shows when validate checkbox is checked */}
                              {validateImmediately && (
                                <Box 
                                  sx={{ 
                                    mb: 3, 
                                    p: 2, 
                                    bgcolor: 'rgba(102, 126, 234, 0.08)',
                                    borderRadius: 2,
                                    border: '1px solid rgba(102, 126, 234, 0.3)'
                                  }}
                                >
                                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                    {t('validateNow.quantityTitle')}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                                    {t('validateNow.quantityHint')}
                                  </Typography>
                                  
                                  <Box display="flex" alignItems="center" gap={2}>
                                    <IconButton
                                      onClick={() => {
                                        if (validationQuantity > 0) {
                                          setValidationQuantity(validationQuantity - 1);
                                        }
                                      }}
                                      disabled={validationQuantity <= 0}
                                      sx={{
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        '&:hover': { bgcolor: 'primary.dark' },
                                        '&:disabled': { bgcolor: 'grey.300', color: 'grey.500' }
                                      }}
                                    >
                                      <Remove />
                                    </IconButton>
                                    
                                    <Box 
                                      sx={{ 
                                        minWidth: 80, 
                                        textAlign: 'center',
                                        bgcolor: 'white',
                                        borderRadius: 1,
                                        py: 1,
                                        px: 2,
                                        border: '2px solid',
                                        borderColor: 'primary.main'
                                      }}
                                    >
                                      <Typography variant="h5" fontWeight="bold" color="primary">
                                        {validationQuantity}
                                      </Typography>
                                    </Box>
                                    
                                    <IconButton
                                      onClick={() => {
                                        const totalTickets = selectedTickets.reduce((sum, tk) => sum + tk.numberOfTickets, 0);
                                        if (validationQuantity < totalTickets) {
                                          setValidationQuantity(validationQuantity + 1);
                                        }
                                      }}
                                      disabled={validationQuantity >= selectedTickets.reduce((sum, tk) => sum + tk.numberOfTickets, 0)}
                                      sx={{
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        '&:hover': { bgcolor: 'primary.dark' },
                                        '&:disabled': { bgcolor: 'grey.300', color: 'grey.500' }
                                      }}
                                    >
                                      <Add />
                                    </IconButton>
                                    
                                    <Typography variant="body2" color="text.secondary">
                                      {t('validateNow.ofTickets', { count: selectedTickets.reduce((sum, tk) => sum + tk.numberOfTickets, 0) })}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                              
                              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                                {t('summary.paymentMethod')}
                              </Typography>
                              <ButtonGroup fullWidth variant="outlined" sx={{ mb: 2 }}>
                                {/* DV-313: cash is no longer accepted. */}
                                {CASH_PAYMENTS_ENABLED && (
                                  <Button
                                    variant={formData.paymentMethod === 'CASH' ? 'contained' : 'outlined'}
                                    onClick={() => handlePaymentMethodChange('CASH')}
                                    startIcon={<MoneyOff />}
                                  >
                                    {t('summary.cash')}
                                  </Button>
                                )}
                                <Button
                                  variant={formData.paymentMethod === 'SQUARE' ? 'contained' : 'outlined'}
                                  onClick={() => handlePaymentMethodChange('SQUARE')}
                                  startIcon={<CreditCardIcon />}
                                >
                                  {t('summary.card')}
                                </Button>
                              </ButtonGroup>
                              
                              {showPaymentForm && formData.paymentMethod === 'SQUARE' && (
                                <Box sx={{ mb: 2 }}>
                                  <TextField
                                    fullWidth
                                    label={t('summary.cardholderName')}
                                    value={cardholderName}
                                    onChange={(e) => setCardholderName(e.target.value)}
                                    disabled={loading}
                                    error={!!formErrors.cardholderName}
                                    helperText={formErrors.cardholderName}
                                    sx={{ mb: 2 }}
                                  />
                                  
                                  {loading ? (
                                    <Box display="flex" justifyContent="center" py={3}>
                                      <CircularProgress />
                                    </Box>
                                  ) : (
                                    <PaymentForm
                                      applicationId={config.SQUARE_APPLICATION_ID}
                                      locationId={config.SQUARE_LOCATION_ID}
                                      cardTokenizeResponseReceived={handleSquareTokenReceived}
                                      createPaymentRequest={() => ({
                                        countryCode: 'US',
                                        currencyCode: 'USD',
                                        total: {
                                          amount: calculateTotal().toFixed(2),
                                          label: t('summary.paymentRequestLabel')
                                        }
                                      })}
                                    >
                                      <CreditCard />
                                    </PaymentForm>
                                  )}
                                </Box>
                              )}
                              
                              {formData.paymentMethod === 'CASH' && (
                                <Button
                                  type="submit"
                                  variant="contained"
                                  size="large"
                                  fullWidth
                                  disabled={loading || selectedTickets.length === 0 || !iboValidated}
                                  sx={{ py: 1.5 }}
                                  onClick={handleSubmit}
                                >
                                  {loading ? <CircularProgress size={24} /> : t('summary.sellButton', { amount: formatCurrency(calculateTotal()) })}
                                </Button>
                              )}
                            </>
                          ) : (
                            <Typography color="text.secondary" textAlign="center" py={4}>
                              {t('summary.noTicketsSelected')}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Fade>
                  )}
                  
                  {!iboValidated && (
                    <Card>
                      <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <Person sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          {t('ibo.validateTitle')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('ibo.validateHint')}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                </Stack>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }
  
  // IBO-First Mode (Original Flow)
  // Main sell ticket form
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ mb: 2 }}
          >
            {t('common.back')}
          </Button>
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            {t('iboFirst.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('iboFirst.subtitle')}
          </Typography>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {/* QR Scanner Dialog */}
        {showQRScanner && (
          <Dialog
            open={true}
            onClose={closeQRScanner}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={false}
            TransitionProps={{
              onExited: () => {
                // Final cleanup after dialog animation completes
                console.log('Dialog exited - final cleanup');
                const videoElements = document.querySelectorAll('video');
                videoElements.forEach(video => {
                  if (video.srcObject) {
                    const stream = video.srcObject;
                    stream.getTracks().forEach(track => track.stop());
                    video.srcObject = null;
                  }
                });
              }
            }}
          >
            <DialogTitle>
              {t('qr.title')}
              <IconButton
                onClick={closeQRScanner}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ position: 'relative', width: '100%', height: 400, bgcolor: 'black', borderRadius: 2, overflow: 'hidden' }}>
                <QrReader
                  key={scannerKey}
                  constraints={{
                    facingMode: 'environment',
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                  }}
                  onResult={(result, error) => {
                    if (result) {
                      handleQRScan(result);
                    }
                  }}
                  scanDelay={300}
                  videoId="sell-ticket-qr-video"
                  videoStyle={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    position: 'absolute',
                    top: 0,
                    left: 0
                  }}
                />
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 200,
                  height: 200,
                  border: '3px solid',
                  borderColor: 'primary.main',
                  borderRadius: 2,
                  pointerEvents: 'none',
                  zIndex: 10
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              {t('qr.hint')}
            </Typography>
          </DialogContent>
        </Dialog>
        )}
        
        <form onSubmit={handleSubmit}>
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', lg: 'row' },
              gap: 3,
              alignItems: 'flex-start'
            }}
          >
            {/* Left Section - IBO, Team, Events (70% width) */}
            <Box 
              sx={{
                flex: { xs: '1 1 100%', lg: '0 0 calc(70% - 12px)' },
                minWidth: 0,
                width: { xs: '100%', lg: 'calc(70% - 12px)' }
              }}
            >
              <Stack spacing={3}>
                {/* IBO Number Card */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      {t('ibo.label')}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        fullWidth
                        name="iboNumber"
                        value={formData.iboNumber}
                        onChange={handleChange}
                        placeholder={t('ibo.placeholder')}
                        disabled={fetchingUserData}
                        error={!!formErrors.iboNumber}
                        helperText={formErrors.iboNumber}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={openQRScanner}
                                disabled={fetchingUserData}
                                color="primary"
                              >
                                <QrCodeScanner />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                      {formData.iboNumber && !iboValidated && (
                        <Button
                          variant="contained"
                          onClick={handleIBOValidation}
                          disabled={fetchingUserData}
                          sx={{ minWidth: 120 }}
                        >
                          {fetchingUserData ? <CircularProgress size={24} /> : t('common.validate')}
                        </Button>
                      )}
                    </Stack>
                    {iboValidated && (
                      <Alert 
                        severity={userData ? "success" : "info"} 
                        sx={{ mt: 2 }} 
                        icon={<CheckCircle />}
                      >
                        {userData 
                          ? t('ibo.valid', { name: userData.iboName || userData.email })
                          : t('ibo.validatedSelectTeam')}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
                
                {/* Team Name Card */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      {t('team.label')}
                    </Typography>
                    
                    {iboValidated && userData && userData.teamName ? (
                      <>
                        <Chip
                          label={userData.teamName}
                          color="primary"
                          size="large"
                          sx={{ fontSize: '1rem', py: 2.5, px: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          {t('team.fromProfile')}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                          {teams.map(team => (
                            <Chip
                              key={team.id}
                              label={team.name}
                              onClick={() => {
                                setFormData(prev => ({ ...prev, teamName: team.name }));
                                filterEventsByTeam(team.name);
                              }}
                              color={formData.teamName === team.name ? 'primary' : 'default'}
                              variant={formData.teamName === team.name ? 'filled' : 'outlined'}
                              sx={{
                                fontSize: '0.95rem',
                                py: 2.5,
                                px: 2,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: 2
                                }
                              }}
                              disabled={!iboValidated}
                            />
                          ))}
                        </Box>
                        {!iboValidated && (
                          <Typography variant="caption" color="text.secondary">
                            {t('team.validateIboFirst')}
                          </Typography>
                        )}
                        {iboValidated && teams.length === 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {t('team.noTeams')}
                          </Typography>
                        )}
                        {iboValidated && teams.length > 0 && !formData.teamName && (
                          <Typography variant="caption" color="warning.main">
                            {t('team.selectTeam')}
                          </Typography>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
                
                {/* Events Card */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      {t('iboFirst.selectEvent')}
                    </Typography>
                    <Grid container spacing={{ xs: 0.75, sm: 1, md: 1.5 }}>
                      {events.map(event => (
                        <Grid item xs={6} sm={6} md={6} lg={6} key={event.id} sx={{ display: 'flex' }}>
                          <Card
                            sx={{
                              cursor: 'pointer',
                              border: 2,
                              borderColor: parseInt(formData.eventId) === event.id ? 'primary.main' : 'transparent',
                              transition: 'all 0.3s',
                              overflow: 'hidden',
                              width: '100%',
                              minHeight: { xs: 220, sm: 280, md: 300 },
                              display: 'flex',
                              flexDirection: 'column',
                              '&:hover': {
                                borderColor: 'primary.light',
                                transform: 'translateY(-4px)',
                                boxShadow: 4
                              }
                            }}
                            onClick={() => handleEventSelect(event)}
                          >
                            <Box sx={{ position: 'relative' }}>
                              <CardMedia
                                component="img"
                                sx={{ height: { xs: 120, sm: 140, md: 140 } }}
                                image={event.imageUrl || `https://via.placeholder.com/400x200?text=${encodeURIComponent(event.name)}`}
                                alt={event.name}
                              />
                              {/* Overlay badges on image */}
                              <Box sx={{ position: 'absolute', top: 8, left: 8, right: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Chip
                                  label={enumLabel('eventTypes', event.eventType)}
                                  size="small"
                                  sx={{
                                    backgroundColor: getEventTypeColor(event.eventType),
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                    height: { xs: 20, sm: 24 },
                                    textTransform: 'uppercase'
                                  }}
                                />
                            </Box>
                          </Box>
                          <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 2 }, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <Box>
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                              <CalendarToday sx={{ fontSize: { xs: 12, sm: 14 }, color: 'primary.main' }} />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                {new Date(event.startDate).toLocaleDateString(intlLocale(), { 
                                  weekday: 'short', 
                                  month: 'short', 
                                  day: 'numeric',
                                  hour: 'numeric',
                                    minute: '2-digit',
                                    timeZone: event.timezone || 'America/Los_Angeles'
                                })}
                              </Typography>
                            </Box>
                            <Typography 
                              variant="subtitle1" 
                              fontWeight="bold" 
                              gutterBottom 
                              sx={{ 
                                fontSize: { xs: '0.85rem', sm: '1rem' },
                                lineHeight: 1.3,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                  minWidth: 0,
                                  minHeight: { xs: '2.2rem', sm: '2.6rem' }
                              }}
                            >
                              {event.name}
                            </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                              <LocationOn sx={{ fontSize: { xs: 12, sm: 14 }, color: 'text.secondary', flexShrink: 0 }} />
                              <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                noWrap
                                sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                              >
                                {event.location}
                              </Typography>
                            </Box>
                          </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                    {events.length === 0 && (
                      <Typography color="text.secondary" textAlign="center" py={4}>
                        {t('iboFirst.noEventsForTeam')}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Stack>
            </Box>
            
            {/* Right Section - Tickets & Payment (30% width) */}
            <Box 
              sx={{
                flex: { xs: '1 1 100%', lg: '0 0 calc(30% - 12px)' },
                minWidth: 0,
                width: { xs: '100%', lg: 'calc(30% - 12px)' }
              }}
            >
              <Box sx={{ position: { lg: 'sticky' }, top: { lg: 100 } }}>
                <Stack spacing={3}>
                  {/* Placeholder when no event selected */}
                  {!selectedEvent && (
                    <Card>
                      <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <EventIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          {t('iboFirst.selectEventTitle')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('iboFirst.selectEventHint')}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Ticket Selection Card */}
                  {selectedEvent && selectedEvent.ticketPriceInfos && selectedEvent.ticketPriceInfos.length > 0 && (
                    <Fade in={true} timeout={500}>
                      <Card ref={ticketSectionRef}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom fontWeight="bold">
                            {t('tickets.title')}
                          </Typography>
                        <Stack spacing={2}>
                          {selectedEvent.ticketPriceInfos
                            .filter(ticket => ticket.active)
                            .sort((a, b) => a.displayOrder - b.displayOrder)
                            .map(ticketInfo => {
                              const effectivePrice = getEffectivePrice(ticketInfo);
                              const selectedTicket = selectedTickets.find(tk => tk.ticketPriceInfoId === ticketInfo.id);
                              const quantity = selectedTicket ? selectedTicket.numberOfTickets : 0;
                              
                              return (
                                <Paper key={ticketInfo.id} elevation={1} sx={{ p: 2 }}>
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                      {ticketInfo.name}
                                    </Typography>
                                    <Typography variant="subtitle1" color="primary" fontWeight="bold">
                                      {formatCurrency(effectivePrice)}
                                    </Typography>
                                  </Box>
                                  {ticketInfo.description && (
                                    <Typography variant="caption" color="text.secondary" mb={1} display="block">
                                      {ticketInfo.description}
                                    </Typography>
                                  )}
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                                    <Chip label={enumLabel('ticketTypes', ticketInfo.ticketType)} size="small" />
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleTicketChange(ticketInfo.id, Math.max(0, quantity - 1))}
                                        disabled={quantity === 0}
                                      >
                                        <Remove fontSize="small" />
                                      </IconButton>
                                      <Typography variant="body1" fontWeight="bold" sx={{ minWidth: 25, textAlign: 'center' }}>
                                        {quantity}
                                      </Typography>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleTicketChange(ticketInfo.id, Math.min(ticketInfo.availableQuantity, quantity + 1))}
                                        disabled={quantity >= ticketInfo.availableQuantity}
                                      >
                                        <Add fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                    {t('tickets.available', { count: ticketInfo.availableQuantity })}
                                  </Typography>
                                </Paper>
                              );
                            })}
                        </Stack>
                        {formErrors.tickets && (
                          <Alert severity="error" sx={{ mt: 2 }}>
                            {formErrors.tickets}
                          </Alert>
                        )}
                      </CardContent>
                    </Card>
                    </Fade>
                  )}
                  
                  {/* Payment Summary Card */}
                  {selectedEvent && (
                    <Fade in={true} timeout={700}>
                      <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom fontWeight="bold">
                        {t('summary.title')}
                      </Typography>
                      
                      {selectedTickets.length > 0 ? (
                        <>
                          <Stack spacing={1} sx={{ mb: 2 }}>
                            {selectedTickets.map(selectedTicket => {
                              const ticketInfo = selectedEvent.ticketPriceInfos.find(
                                info => info.id === selectedTicket.ticketPriceInfoId
                              );
                              if (!ticketInfo) return null;
                              
                              const effectivePrice = getEffectivePrice(ticketInfo);
                              const subtotal = effectivePrice * selectedTicket.numberOfTickets;
                              const tax = (subtotal * ticketInfo.taxPercentage) / 100;
                              const serviceCharge = ticketInfo.serviceCharge * selectedTicket.numberOfTickets;
                              
                              return (
                                <Box key={selectedTicket.ticketPriceInfoId}>
                                  <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2">
                                      {t('summary.lineItem', { name: ticketInfo.name, quantity: selectedTicket.numberOfTickets })}
                                    </Typography>
                                    <Typography variant="body2" fontWeight="bold">
                                      {formatCurrency(subtotal)}
                                    </Typography>
                                  </Box>
                                  {tax > 0 && (
                                    <Box display="flex" justifyContent="space-between" ml={2}>
                                      <Typography variant="caption" color="text.secondary">
                                        {t('summary.tax', { percent: ticketInfo.taxPercentage })}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {formatCurrency(tax)}
                                      </Typography>
                                    </Box>
                                  )}
                                  {serviceCharge > 0 && (
                                    <Box display="flex" justifyContent="space-between" ml={2}>
                                      <Typography variant="caption" color="text.secondary">
                                        {t('summary.serviceCharge')}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {formatCurrency(serviceCharge)}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              );
                            })}
                          </Stack>
                          
                          <Divider sx={{ my: 2 }} />
                          
                          <Box display="flex" justifyContent="space-between" mb={3}>
                            <Typography variant="h6" fontWeight="bold">
                              {t('summary.total')}
                            </Typography>
                            <Typography variant="h6" color="primary" fontWeight="bold">
                              {formatCurrency(calculateTotal())}
                            </Typography>
                          </Box>
                          
                          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                            {t('summary.paymentMethod')}
                          </Typography>
                          <ButtonGroup fullWidth variant="outlined" sx={{ mb: 2 }}>
                            {/* DV-313: cash is no longer accepted. */}
                            {CASH_PAYMENTS_ENABLED && (
                              <Button
                                variant={formData.paymentMethod === 'CASH' ? 'contained' : 'outlined'}
                                onClick={() => handlePaymentMethodChange('CASH')}
                                startIcon={<MoneyOff />}
                              >
                                {t('summary.cash')}
                              </Button>
                            )}
                            <Button
                              variant={formData.paymentMethod === 'SQUARE' ? 'contained' : 'outlined'}
                              onClick={() => handlePaymentMethodChange('SQUARE')}
                              startIcon={<CreditCardIcon />}
                            >
                              {t('summary.card')}
                            </Button>
                          </ButtonGroup>
                          
                          {showPaymentForm && formData.paymentMethod === 'SQUARE' && (
                            <Box sx={{ mb: 2 }}>
                              <TextField
                                fullWidth
                                label={t('summary.cardholderName')}
                                value={cardholderName}
                                onChange={(e) => setCardholderName(e.target.value)}
                                disabled={loading}
                                error={!!formErrors.cardholderName}
                                helperText={formErrors.cardholderName}
                                sx={{ mb: 2 }}
                              />
                              
                              {loading ? (
                                <Box display="flex" justifyContent="center" py={3}>
                                  <CircularProgress />
                                </Box>
                              ) : (
                                <PaymentForm
                                  applicationId={config.SQUARE_APPLICATION_ID}
                                  locationId={config.SQUARE_LOCATION_ID}
                                  cardTokenizeResponseReceived={handleSquareTokenReceived}
                                  createPaymentRequest={() => ({
                                    countryCode: 'US',
                                    currencyCode: 'USD',
                                    total: {
                                      amount: calculateTotal().toFixed(2),
                                      label: t('summary.paymentRequestLabel')
                                    }
                                  })}
                                >
                                  <CreditCard />
                                </PaymentForm>
                              )}
                            </Box>
                          )}
                          
                          {formData.paymentMethod === 'CASH' && (
                            <Button
                              type="submit"
                              variant="contained"
                              size="large"
                              fullWidth
                              disabled={loading || selectedTickets.length === 0 || !iboValidated}
                              sx={{ py: 1.5 }}
                            >
                              {loading ? <CircularProgress size={24} /> : t('summary.sellButton', { amount: formatCurrency(calculateTotal()) })}
                            </Button>
                          )}
                        </>
                      ) : (
                        <Typography color="text.secondary" textAlign="center" py={4}>
                          {t('summary.noTicketsSelected')}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                      </Fade>
                  )}
                </Stack>
              </Box>
            </Box>
          </Box>
        </form>
      </Container>

      {/* Cash Tracker Floating Widget & Panel */}
      <CashFloatingWidget
        onClick={() => setCashTrackerOpen(true)}
        refreshTrigger={cashRefreshTrigger}
      />
      <CashTrackerPanel
        open={cashTrackerOpen}
        onClose={() => setCashTrackerOpen(false)}
        refreshTrigger={cashRefreshTrigger}
      />
    </Box>
  );
};

export default SellTicketPage;

